/**
 * KommunikationPage — Unified Inbox (Superchat/HubSpot-Style)
 * 
 * ALLES IN EINER PIPELINE:
 * - E-Mails, Team-Chat, WhatsApp, Instagram, Telefon — alles in einer chronologischen Liste
 * - E-Mail-Ordner als Dropdown-Filter (nicht als eigene Sidebar)
 * - Offen/Später/Erledigt als Hauptsteuerung
 * 
 * LAYOUT: 3-Spalten wie Superchat
 * - LINKS: Kanal-Sidebar (Icons, Hover-Expand) + Unterhaltungsliste
 * - MITTE: Nachrichtenbereich (Chat oder E-Mail-Detail)
 * - RECHTS: Kontaktdetails + CRM-Verknüpfung
 */

import { useState, useCallback, useRef, useEffect, useMemo, Component, type ReactNode, type ErrorInfo } from 'react';
import DocAIPanel from '@/components/DocAIPanel';
import { useDocAI } from '@/hooks/useDocAI';
import type { DocAIAktion } from '@/hooks/useDocAI';
import {
  MessageSquare, Mail, Phone, Send, Users,
  Search, Plus, Filter, ChevronRight, ChevronLeft, ChevronDown,
  Clock, CheckCheck, AlertCircle,
  FileText, Paperclip, MoreHorizontal,
  X, ArrowLeft, Loader2, Image as ImageIcon,
  Smile, Mic, Download, Trash2, ListTodo,
  UserPlus, Hash, Lock, Globe, File,
  Video, Music, Archive, Star, Reply, Forward,
  Building2, UserCircle, Inbox, SendHorizontal, Tag,
  MailOpen, Eye, EyeOff, Zap, RefreshCw, MoreVertical, Pencil, CornerUpLeft,
  MessageCircle, Instagram,
  AtSign, PhoneCall, PhoneIncoming, PhoneMissed, PhoneOutgoing,
  ExternalLink, Link2, Bookmark, BookmarkCheck,
  CircleDot, Circle, CheckCircle2, Timer, SlidersHorizontal, CalendarClock,
  Type, Bold, Italic, Underline, List, ListOrdered, AlignLeft, Undo2, Redo2,
  AlertTriangle, Info, ShieldCheck, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { useChat, useNotifications, type ChatRoom, type ChatMessage } from '@/hooks/useChat';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useGmail, type GmailMessage, type GmailLoadProgress, type EmailAttachment } from '@/hooks/useGmail';
import { KI_AGENT_ID } from '@/lib/kiAgent';
import { getMietGoSignatureHtml } from '@/lib/mietgo-footer';
import { getLenneglasSignatureHtml } from '@/lib/lenneglas-footer';
import CreateTaskFromEmailDialog from '@/components/CreateTaskFromEmailDialog';
import { useMobileBackHandler, useMobileDetailBackHandler } from '@/hooks/useMobileBackHandler';
import { useCRMKontaktSuche, type CRMKontakt } from '@/hooks/useCRM';
import { useEmailAccounts, useEmailMessages, useEmailFilters, useEmailSignatures, type EmailAccount } from '@/hooks/useEmailAccounts';
import EmailAccountSelector, { AccountIndicator, getCompanyColor } from '@/components/EmailAccountSelector';
import EmailFilterDialog from '@/components/EmailFilterDialog';
import EmailSettingsPanel from '@/components/EmailSettingsPanel';
import { useConversationStatus, type ConversationStatusType } from '@/hooks/useConversationStatus';
import { useMessaging, type TwilioMessage, type MessagingConversation } from '@/hooks/useMessaging';
import MessagingArea, { MessagingEmptyState, NewMessageDialog } from '@/components/MessagingArea';
import { usePhoneSystem, type PhoneCall as PhoneCallType } from '@/hooks/usePhoneSystem';
import { IncomingCallPopup, ActiveCallView, PhoneDialer, CallLogList, CallDetailPanel, PhoneStatsBanner, DurchwahlenPanel } from '@/components/PhoneSystem';

// ─── Types ─────────────────────────────────────────────────────────────────

type ChannelId = 'alle' | 'chat' | 'email' | 'whatsapp' | 'sms' | 'instagram' | 'telefon';
type ConversationStatus = 'offen' | 'spaeter' | 'erledigt';

interface UnifiedConversation {
  id: string;
  channel: ChannelId;
  name: string;
  subtitle?: string;
  preview?: string;
  timestamp: string;
  unreadCount: number;
  status: ConversationStatus;
  avatarInitials?: string;
  avatarUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactFirma?: string;
  chatRoom?: ChatRoom;
  emailMessage?: GmailMessage;
  emailThreadId?: string;
  emailAccountId?: string;
  emailAccountColor?: string;
  lastMessage?: string;
  originalId?: string;
}

// ─── E-Mail Ordner ─────────────────────────────────────────────────────────

const EMAIL_FOLDERS = [
  { id: 'INBOX', label: 'Posteingang', icon: Inbox },
  { id: 'SENT', label: 'Gesendet', icon: Send },
  { id: 'STARRED', label: 'Markiert', icon: Star },
  { id: 'DRAFT', label: 'Entwürfe', icon: Mail },
  { id: 'SPAM', label: 'Spam', icon: AlertTriangle },
  { id: 'TRASH', label: 'Papierkorb', icon: Trash2 },
  { id: 'ALL', label: 'Alle E-Mails', icon: Archive },
];

// ─── Kanal-Konfiguration ───────────────────────────────────────────────────

const CHANNEL_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string; bgColor: string; available: boolean }> = {
  alle:      { label: 'Alle',       icon: Inbox,          color: '#374151', bgColor: '#F3F4F6', available: true },
  chat:      { label: 'Team Chat',  icon: MessageSquare,  color: '#C41E3A', bgColor: '#FEF2F2', available: true },
  email:     { label: 'E-Mail',     icon: Mail,           color: '#2563EB', bgColor: '#EFF6FF', available: true },
  whatsapp:  { label: 'WhatsApp',   icon: MessageCircle,  color: '#25D366', bgColor: '#F0FDF4', available: true },
  sms:       { label: 'SMS',        icon: MessageSquare,  color: '#F59E0B', bgColor: '#FFFBEB', available: true },
  instagram: { label: 'Instagram',  icon: Instagram,      color: '#E1306C', bgColor: '#FDF2F8', available: false },
  telefon:   { label: 'Telefon',    icon: Phone,          color: '#7C3AED', bgColor: '#F5F3FF', available: true },
};

// ─── HTML-Sanitizer für E-Mail-Body ───────────────────────────────────────
// Entfernt potenziell gefährliche HTML-Elemente und Attribute aus E-Mail-HTML
// bevor es in den iframe geschrieben wird (Defense-in-Depth neben sandbox).

function sanitizeEmailHtml(html: string): string {
  // 1. Script-Tags komplett entfernen (inkl. Inhalt)
  let clean = html.replace(/<script[\s\S]*?<\/script>/gi, '');
  // 2. Standalone-Script-Tags
  clean = clean.replace(/<script[^>]*\/?>/gi, '');
  // 3. Event-Handler-Attribute entfernen (on*)
  clean = clean.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '');
  // 4. javascript: URLs in href/src/action
  clean = clean.replace(/(href|src|action)\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi, '$1=""');
  // 4b. vbscript: URLs entfernen
  clean = clean.replace(/(href|src|action)\s*=\s*(?:"vbscript:[^"]*"|'vbscript:[^']*')/gi, '$1=""');
  // 5. data: URLs in src (kann zum Laden externer Ressourcen missbraucht werden, aber base64-Bilder erlauben)
  // 6. <form>-Tags entfernen (Anti-Phishing)
  clean = clean.replace(/<\/?form[^>]*>/gi, '');
  // 7. <input>/<button>/<select>/<textarea> entfernen (Anti-Phishing)
  clean = clean.replace(/<(input|button|select|textarea)[^>]*\/?>/gi, '');
  // 8. <object>/<embed>/<applet>/<iframe> (au\u00dfer eigene) entfernen
  clean = clean.replace(/<\/?(object|embed|applet)[^>]*>/gi, '');
  // 9. <meta http-equiv="refresh"> entfernen
  clean = clean.replace(/<meta[^>]*http-equiv\s*=\s*["']?refresh["']?[^>]*>/gi, '');
  // 10. <link rel="import"> und <base> entfernen (Anti-Redirect)
  clean = clean.replace(/<link[^>]*rel\s*=\s*["']?import["']?[^>]*>/gi, '');
  clean = clean.replace(/<base[^>]*>/gi, '');
  // 11. SVG onload und andere SVG-basierte XSS
  clean = clean.replace(/<svg[^>]*on\w+[^>]*>/gi, '<svg>');
  return clean;
}

// ─── Hilfsfunktionen ───────────────────────────────────────────────────────

function formatTime(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    if (days === 1) return 'Gestern';
    if (days < 7) return d.toLocaleDateString('de-DE', { weekday: 'short' });
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return ''; }
}

function formatMessageTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatDateSeparator(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Heute';
  if (days === 1) return 'Gestern';
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
}

function formatFullDate(dateStr: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return dateStr; }
}

function getFileIcon(fileType: string | null) {
  if (!fileType) return File;
  if (fileType.startsWith('image/')) return ImageIcon;
  if (fileType.startsWith('video/')) return Video;
  if (fileType.startsWith('audio/')) return Music;
  if (fileType.includes('pdf')) return FileText;
  return File;
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getInitials(vorname?: string, nachname?: string): string {
  return `${(vorname || '?')[0]}${(nachname || '?')[0]}`.toUpperCase();
}

function extractName(from: string): string {
  if (!from) return 'Unbekannt';
  const match = from.match(/^"?([^"<]+)"?\s*</);
  if (match) return match[1].trim();
  return from.split('@')[0] || from;
}

function extractEmail(from: string): string {
  if (!from) return '';
  const match = from.match(/<([^>]+)>/);
  if (match) return match[1];
  return from;
}

function getEmailInitials(name: string): string {
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (name[0] || '?').toUpperCase();
}

// ─── Kanal-Sidebar (links, schmal, Hover-Expand) ──────────────────────────

function ChannelSidebar({ 
  activeChannel, 
  onSelectChannel,
  channelCounts,
}: { 
  activeChannel: ChannelId; 
  onSelectChannel: (ch: ChannelId) => void;
  channelCounts: Record<string, number>;
}) {
  const [expanded, setExpanded] = useState(false);
  const channels: ChannelId[] = ['alle', 'chat', 'email', 'whatsapp', 'sms', 'instagram', 'telefon'];

  return (
    <nav
      className="hidden lg:flex flex-col border-r border-gray-100 bg-white transition-all duration-200 ease-in-out flex-shrink-0 relative z-10"
      style={{ width: expanded ? 200 : 64 }}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      aria-label="Kommunikationskan\u00e4le"
    >
      <div className="h-12 flex items-center justify-center border-b border-gray-100 flex-shrink-0">
        {expanded ? (
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Kan\u00e4le</span>
        ) : (
          <SlidersHorizontal className="w-4 h-4 text-gray-400" />
        )}
      </div>
      <div className="flex-1 py-1.5 space-y-0.5 overflow-hidden">
        {channels.map(chId => {
          const cfg = CHANNEL_CONFIG[chId];
          const Icon = cfg.icon;
          const isActive = activeChannel === chId;
          const count = channelCounts[chId] || 0;
          const isAvailable = cfg.available;

          return (
            <button
              key={chId}
              onClick={() => isAvailable && onSelectChannel(chId)}
              disabled={!isAvailable}
              aria-current={isActive ? 'page' : undefined}
              aria-label={`${cfg.label}${count > 0 ? `, ${count} ungelesen` : ''}${!isAvailable ? ' (demn\u00e4chst verf\u00fcgbar)' : ''}`}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 transition-all duration-150 relative group ${
                isActive ? 'bg-gray-50' : isAvailable ? 'hover:bg-gray-50/50' : 'opacity-30 cursor-not-allowed'
              }`}
            >
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-5 rounded-r-full" style={{ backgroundColor: cfg.color }} />
              )}
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                  isActive ? '' : 'bg-gray-50 group-hover:bg-gray-100'
                }`}
                style={isActive ? { backgroundColor: cfg.bgColor } : undefined}
              >
                <Icon className="w-4 h-4" style={{ color: isActive ? cfg.color : '#9CA3AF' }} />
              </div>
              {expanded && (
                <div className="flex-1 flex items-center justify-between min-w-0">
                  <span className={`text-[13px] font-medium truncate ${isActive ? 'text-gray-800' : 'text-gray-600'}`}>
                    {cfg.label}
                  </span>
                  {count > 0 && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white flex-shrink-0" style={{ backgroundColor: cfg.color }}>
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                  {!isAvailable && (
                    <span className="text-[9px] bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded font-medium">Bald</span>
                  )}
                </div>
              )}
              {!expanded && count > 0 && (
                <div className="absolute top-0.5 right-1 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-semibold text-white" style={{ backgroundColor: cfg.color }}>
                  {count > 9 ? '9+' : count}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// ─── Mobile Kanal-Tabs ─────────────────────────────────────────────────────

function MobileChannelTabs({ activeChannel, onSelectChannel, channelCounts }: {
  activeChannel: ChannelId;
  onSelectChannel: (ch: ChannelId) => void;
  channelCounts: Record<string, number>;
}) {
  const channels: ChannelId[] = ['alle', 'chat', 'email', 'whatsapp', 'sms', 'instagram', 'telefon'];

  return (
    <nav className="lg:hidden flex items-center gap-1 px-2 overflow-x-auto border-b border-gray-200 bg-white flex-shrink-0" aria-label="Kommunikationskanäle">
      {channels.map(chId => {
        const cfg = CHANNEL_CONFIG[chId];
        const Icon = cfg.icon;
        const isActive = activeChannel === chId;
        const count = channelCounts[chId] || 0;

        return (
          <button
            key={chId}
            onClick={() => cfg.available && onSelectChannel(chId)}
            disabled={!cfg.available}
            aria-current={isActive ? 'page' : undefined}
            aria-label={`${cfg.label}${count > 0 ? `, ${count} ungelesen` : ''}`}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors min-h-[40px] ${
              isActive ? 'text-gray-900' : cfg.available ? 'border-transparent text-gray-500 hover:text-gray-700' : 'border-transparent text-gray-400 cursor-not-allowed'
            }`}
            style={isActive ? { borderColor: cfg.color, color: cfg.color } : undefined}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{cfg.label}</span>
            {count > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: cfg.color }}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

// ─── Unterhaltungsliste (Spalte 2) — Unified Pipeline ─────────────────────

function ConversationList({
  conversations,
  activeId,
  onSelect,
  statusFilter,
  onStatusFilter,
  searchQuery,
  onSearchQuery,
  onNewChat,
  onNewEmail,
  onNewWhatsApp,
  onNewSMS,
  onNewCall,
  activeChannel,
  emailFolder,
  onEmailFolder,
  onRefreshEmails,
  emailsLoading,
  loadProgress,
  emailAccounts,
  selectedEmailAccountId,
  onSelectEmailAccount,
  onOpenFilters,
  onOpenSettings,
  onQuickStatus,
  lastSyncTime,
  hasMore,
  onLoadMore,
  loadingMore,
  phoneLoading,
}: {
  conversations: UnifiedConversation[];
  activeId: string | null;
  onSelect: (conv: UnifiedConversation) => void;
  statusFilter: ConversationStatus;
  onStatusFilter: (s: ConversationStatus) => void;
  searchQuery: string;
  onSearchQuery: (q: string) => void;
  onNewChat: () => void;
  onNewEmail: () => void;
  onNewWhatsApp?: () => void;
  onNewSMS?: () => void;
  onNewCall?: () => void;
  activeChannel: ChannelId;
  emailFolder: string;
  onEmailFolder: (folder: string) => void;
  onRefreshEmails: () => void;
  emailsLoading: boolean;
  loadProgress?: GmailLoadProgress;
  emailAccounts?: any[];
  selectedEmailAccountId?: string | null;
  onSelectEmailAccount?: (id: string) => void;
  onOpenFilters?: () => void;
  onOpenSettings?: () => void;
  onQuickStatus?: (convId: string, status: ConversationStatus) => void;
  lastSyncTime?: string | null;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  phoneLoading?: boolean;
}) {
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const statusTabs: { id: ConversationStatus; label: string }[] = [
    { id: 'offen', label: 'Offen' },
    { id: 'spaeter', label: 'Später' },
    { id: 'erledigt', label: 'Erledigt' },
  ];

  const filtered = conversations.filter(c => {
    // Status-Filter: Nur Konversationen mit passendem Status anzeigen
    if (c.status !== statusFilter) return false;
    // Account-Filter: Wenn ein spezifisches E-Mail-Konto ausgewählt ist, nur dessen Mails zeigen
    if (selectedEmailAccountId && selectedEmailAccountId !== '__all__' && c.channel === 'email') {
      const selectedAcc = emailAccounts?.find((a: any) => a.id === selectedEmailAccountId);
      if (selectedAcc?.email && c.emailAccountId) {
        // Prüfe ob die Konversation zum ausgewählten Konto gehört
        if (c.emailAccountId !== selectedEmailAccountId) return false;
      } else if (selectedAcc?.email) {
        // Fallback: Prüfe ob die E-Mail-Adresse des Kontos in from/to vorkommt
        const accEmail = selectedAcc.email.toLowerCase();
        const emailMsg = c.emailMessage;
        if (emailMsg) {
          const from = (emailMsg.from || '').toLowerCase();
          const to = (emailMsg.to || '').toLowerCase();
          const matchesAccount = from.includes(accEmail) || to.includes(accEmail);
          if (!matchesAccount) return false;
        }
      }
    }
    // Suchfilter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return c.name.toLowerCase().includes(q) || 
             c.preview?.toLowerCase().includes(q) ||
             c.subtitle?.toLowerCase().includes(q);
    }
    return true;
  });

  const currentFolder = EMAIL_FOLDERS.find(f => f.id === emailFolder) || EMAIL_FOLDERS[0];
  const CurrentFolderIcon = currentFolder.icon;

  return (
    <div className="w-full sm:w-[340px] lg:w-[380px] border-r border-gray-100 flex flex-col h-full bg-white flex-shrink-0">
      {/* Status-Tabs: Offen / Später / Erledigt */}
      <div className="flex items-center border-b border-gray-100 flex-shrink-0 px-1">
        {statusTabs.map(tab => {
          const count = conversations.filter(c => c.status === tab.id).length;
          return (
            <button
              key={tab.id}
              onClick={() => onStatusFilter(tab.id)}
              className={`flex-1 text-center py-3 text-[13px] font-semibold transition-all border-b-2 ${
                statusFilter === tab.id
                  ? 'border-[#C41E3A] text-[#C41E3A]'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span className={`ml-1 text-[11px] tabular-nums ${
                  statusFilter === tab.id ? 'text-[#C41E3A]' : 'text-gray-400'
                }`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* E-Mail-Konto & Ordner-Filter (nur wenn E-Mail-Kanal aktiv oder "Alle") */}
      {(activeChannel === 'email' || activeChannel === 'alle') && (
        <div className="px-3 py-2 border-b border-gray-100 flex-shrink-0 space-y-2">
          {/* Konto-Auswahl (Multi-Account) */}
          {emailAccounts && emailAccounts.length > 0 && onSelectEmailAccount && (
            <EmailAccountSelector
              accounts={emailAccounts}
              selectedAccountId={selectedEmailAccountId || null}
              onSelect={onSelectEmailAccount}
              showLabel={true}
              showAllOption={true}
            />
          )}
          <div className="flex items-center gap-2">
            {/* Ordner-Dropdown */}
            <div className="relative flex-1">
              <button
                onClick={() => setShowFolderDropdown(!showFolderDropdown)}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors text-[13px] font-medium text-gray-600 w-full border border-gray-100"
              >
                <CurrentFolderIcon className="w-3.5 h-3.5 text-gray-400" />
                <span className="flex-1 text-left">{currentFolder.label}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showFolderDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showFolderDropdown && (
                <>
                <div className="fixed inset-0 z-10" onClick={() => setShowFolderDropdown(false)} />
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-100 z-20 py-0.5">
                  {EMAIL_FOLDERS.map(folder => {
                    const FolderIcon = folder.icon;
                    return (
                      <button
                        key={folder.id}
                        onClick={() => { onEmailFolder(folder.id); setShowFolderDropdown(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] transition-colors ${
                          emailFolder === folder.id ? 'bg-gray-50 text-gray-900 font-semibold' : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <FolderIcon className="w-3.5 h-3.5" />
                        {folder.label}
                      </button>
                    );
                  })}
                </div>
                </>
              )}
            </div>
            {/* Filter */}
            {onOpenFilters && (
              <button
                onClick={onOpenFilters}
                className="p-1.5 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
                title="E-Mail-Filter verwalten"
              >
                <Filter className="w-3.5 h-3.5 text-gray-400" />
              </button>
            )}
            {/* Einstellungen */}
            {onOpenSettings && (
              <button
                onClick={onOpenSettings}
                className="p-1.5 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
                title="E-Mail-Einstellungen"
              >
                <SlidersHorizontal className="w-3.5 h-3.5 text-gray-400" />
              </button>
            )}
            {/* Refresh */}
            <button
              onClick={onRefreshEmails}
              className="p-1.5 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
              title={lastSyncTime ? `Letzte Synchronisierung: ${formatTime(lastSyncTime)}` : 'E-Mails aktualisieren'}
              aria-label="E-Mails aktualisieren"
            >
              <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${emailsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {/* Sync-Status */}
          {lastSyncTime && !emailsLoading && (
            <p className="text-[10px] text-gray-400 mt-1 text-right">
              Zuletzt synchronisiert: {formatTime(lastSyncTime)}
            </p>
          )}
        </div>
      )}

      {/* Suche + Neue Unterhaltung */}
      <div className="px-3 py-2 border-b border-gray-50 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 min-h-[34px] border border-gray-100 focus-within:border-gray-200 focus-within:bg-white transition-all">
            <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <input
              value={searchQuery}
              onChange={e => onSearchQuery(e.target.value)}
              placeholder="Suchen..."
              aria-label="Unterhaltungen durchsuchen"
              className="bg-transparent text-[13px] outline-none flex-1 text-gray-700 placeholder-gray-400"
            />
            {searchQuery && (
              <button onClick={() => onSearchQuery('')} className="p-0.5 hover:bg-gray-200 rounded">
                <X className="w-3 h-3 text-gray-400" />
              </button>
            )}
          </div>
          {onOpenFilters && (
            <button onClick={onOpenFilters} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" title="Filter verwalten" aria-label="E-Mail-Filter verwalten">
              <Filter className="w-3.5 h-3.5 text-gray-400" />
            </button>
          )}
          <div className="relative">
            <button
              onClick={() => setShowNewMenu(!showNewMenu)}
              className="p-1.5 bg-[#C41E3A] hover:bg-[#A3172E] rounded-lg transition-colors"
              title="Neue Nachricht"
            >
              <Plus className="w-3.5 h-3.5 text-white" />
            </button>
            {showNewMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowNewMenu(false)} />
                <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                  <button
                    onClick={() => { setShowNewMenu(false); onNewEmail(); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Mail className="w-4 h-4 text-[#C41E3A]" />
                    <div className="text-left">
                      <span className="font-medium">Neue E-Mail</span>
                      <p className="text-[10px] text-gray-400">E-Mail verfassen</p>
                    </div>
                  </button>
                  <button
                    onClick={() => { setShowNewMenu(false); onNewChat(); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <MessageSquare className="w-4 h-4 text-blue-500" />
                    <div className="text-left">
                      <span className="font-medium">Neuer Chat</span>
                      <p className="text-[10px] text-gray-400">Interner Gruppenchat</p>
                    </div>
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={() => { setShowNewMenu(false); onNewWhatsApp?.(); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <MessageCircle className="w-4 h-4 text-[#25D366]" />
                    <div className="text-left">
                      <span className="font-medium">WhatsApp</span>
                      <p className="text-[10px] text-gray-400">WhatsApp-Nachricht senden</p>
                    </div>
                  </button>
                  <button
                    onClick={() => { setShowNewMenu(false); onNewSMS?.(); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <MessageSquare className="w-4 h-4 text-[#F59E0B]" />
                    <div className="text-left">
                      <span className="font-medium">SMS</span>
                      <p className="text-[10px] text-gray-400">SMS-Nachricht senden</p>
                    </div>
                  </button>
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={() => { setShowNewMenu(false); onNewCall?.(); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Phone className="w-4 h-4 text-[#7C3AED]" />
                    <div className="text-left">
                      <span className="font-medium">Telefon</span>
                      <p className="text-[10px] text-gray-400">Anruf starten</p>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Ladefortschritt für E-Mails */}
      {emailsLoading && loadProgress && loadProgress.phase !== 'done' && (
        <div className="px-3 py-3 border-b border-gray-100 bg-blue-50/50 flex-shrink-0">
          <div className="flex items-center gap-2 mb-1.5">
            <Loader2 className="w-4 h-4 text-blue-600 animate-spin flex-shrink-0" />
            <span className="text-xs font-medium text-blue-700">{loadProgress.message}</span>
          </div>
          {loadProgress.total > 0 && (
            <div className="w-full bg-blue-100 rounded-full h-1.5">
              <div 
                className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.round((loadProgress.loaded / loadProgress.total) * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Unterhaltungen */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 && emailsLoading ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-3" />
            <p className="text-sm text-gray-500 font-medium">E-Mails werden geladen...</p>
            {loadProgress && loadProgress.total > 0 && (
              <p className="text-xs text-gray-400 mt-1">{loadProgress.loaded} von {loadProgress.total}</p>
            )}
          </div>
        ) : filtered.length === 0 && activeChannel === 'telefon' && !searchQuery && !phoneLoading && statusFilter === 'offen' ? (
          <div data-testid="phone-empty-state" className="flex flex-col items-center justify-center py-12 px-4">
            <Phone className="w-12 h-12 text-purple-200 mb-3" />
            <p className="text-base text-gray-500 font-semibold">Noch keine Anrufe registriert</p>
            <p className="text-sm text-gray-400 mt-1 text-center">
              Anrufe werden automatisch protokolliert sobald die Telefonanlage live ist.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            {statusFilter === 'offen' ? (
              <CheckCircle2 className="w-12 h-12 text-green-300 mb-3" />
            ) : statusFilter === 'spaeter' ? (
              <Clock className="w-12 h-12 text-orange-300 mb-3" />
            ) : (
              <Inbox className="w-12 h-12 text-gray-300 mb-3" />
            )}
            <p className="text-base text-gray-500 font-semibold">
              {searchQuery ? 'Keine Treffer' : 'Keine Unterhaltungen'}
            </p>
            <p className="text-sm text-gray-400 mt-1 text-center">
              {searchQuery ? `Kein Ergebnis für "${searchQuery}"` :
               statusFilter === 'offen' ? 'Alle Unterhaltungen abgearbeitet — gut gemacht!' :
               statusFilter === 'spaeter' ? 'Keine zurückgestellten Unterhaltungen' :
               'Keine erledigten Unterhaltungen'}
            </p>
            {searchQuery && (
              <button onClick={() => onSearchQuery('')} className="mt-3 text-xs text-[#C41E3A] font-semibold hover:underline">
                Suche zurücksetzen
              </button>
            )}
          </div>
        ) : (
          <>
          {filtered.map(conv => {
            const isActive = conv.id === activeId;
            const cfg = CHANNEL_CONFIG[conv.channel] || CHANNEL_CONFIG.chat;
            const ChannelIcon = cfg.icon;

            return (
              <div
                key={conv.id}
                role="button"
                tabIndex={0}
                aria-selected={isActive}
                aria-label={`${conv.name}${conv.subtitle ? `, ${conv.subtitle}` : ''}${conv.unreadCount > 0 ? `, ${conv.unreadCount} ungelesen` : ''}`}
                className={`relative group w-full flex items-start gap-3 px-4 py-3.5 transition-all text-left border-b border-gray-50 cursor-pointer ${
                  isActive ? 'bg-gray-50/80 border-l-2 border-l-[#C41E3A]' : 'hover:bg-gray-50/50 border-l-2 border-l-transparent'
                }`}
                onClick={() => onSelect(conv)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(conv); } }}
              >
                {/* Avatar mit Kanal-Badge */}
                <div className="relative flex-shrink-0">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-semibold"
                    style={{ backgroundColor: cfg.bgColor, color: cfg.color }}
                  >
                    {conv.avatarInitials || <ChannelIcon className="w-4.5 h-4.5" />}
                  </div>
                  <div
                    className="absolute -bottom-0.5 -right-0.5 w-[18px] h-[18px] rounded-full flex items-center justify-center border-[1.5px] border-white"
                    style={{ backgroundColor: conv.emailAccountColor || cfg.color }}
                  >
                    <ChannelIcon className="w-2.5 h-2.5 text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1.5">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      {conv.emailAccountId && emailAccounts && (() => {
                        const acc = emailAccounts.find(a => a.id === conv.emailAccountId);
                        if (!acc) return null;
                        return <AccountIndicator account={acc} size="xs" />;
                      })()}
                      <span className={`text-[14px] truncate leading-tight ${conv.unreadCount > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                        {conv.name}
                      </span>
                    </div>
                    <span className="text-[11px] text-gray-400 flex-shrink-0 tabular-nums">
                      {formatTime(conv.timestamp)}
                    </span>
                  </div>
                  {conv.subtitle && (
                    <p className="text-[13px] text-gray-500 truncate mt-0.5 leading-tight">
                      {conv.subtitle}
                    </p>
                  )}
                  <div className="flex items-center justify-between gap-1.5 mt-0.5">
                    <p className={`text-[13px] truncate leading-tight ${conv.unreadCount > 0 ? 'text-gray-600 font-medium' : 'text-gray-400'}`}>
                      {conv.preview || 'Keine Nachrichten'}
                    </p>
                    {conv.unreadCount > 0 && (
                      <span
                        className="text-[10px] font-semibold w-5 h-5 rounded-full text-white flex-shrink-0 flex items-center justify-center"
                        style={{ backgroundColor: cfg.color }}
                      >
                        {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                      </span>
                    )}
                  </div>
                </div>

                {/* Quick-Action Buttons (Hover) */}
                {onQuickStatus && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-0.5 bg-white/95 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 px-1 py-0.5">
                    {statusFilter !== 'spaeter' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onQuickStatus(conv.id, 'spaeter'); }}
                        className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-orange-50 transition-colors"
                        title="Sp\u00e4ter"
                      >
                        <Clock className="w-3.5 h-3.5 text-orange-500" />
                      </button>
                    )}
                    {statusFilter !== 'erledigt' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onQuickStatus(conv.id, 'erledigt'); }}
                        className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-green-50 transition-colors"
                        title="Erledigt"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                      </button>
                    )}
                    {statusFilter !== 'offen' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onQuickStatus(conv.id, 'offen'); }}
                        className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-blue-50 transition-colors"
                        title="Zur\u00fcck zu Offen"
                      >
                        <Inbox className="w-3.5 h-3.5 text-blue-500" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {hasMore && (
            <div data-testid="load-more-container" className="px-3 py-3 flex justify-center border-t border-gray-100 bg-white sticky bottom-0 z-[1]">
              <button
                onClick={onLoadMore}
                disabled={loadingMore}
                className="flex items-center gap-1.5 text-[12px] text-gray-500 font-medium hover:text-[#C41E3A] disabled:opacity-50 transition-colors"
              >
                {loadingMore && <Loader2 className="w-3 h-3 animate-spin" />}
                Weitere laden
              </button>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Kontaktdetails Panel (rechts) ─────────────────────────────────────────

function ContactPanel({
  conversation,
  onSetStatus,
  currentStatus,
  statusChangedBy,
  statusChangedAt,
  notes,
  onAddNote,
  onDeleteNote,
  assignments,
  onAssign,
  onUnassign,
  teamMembers: teamMembersList,
  currentUserId,
  docAI,
  selectedConversationMessages,
}: {
  conversation: UnifiedConversation | null;
  onSetStatus?: (status: 'offen' | 'spaeter' | 'erledigt') => void;
  currentStatus?: 'offen' | 'spaeter' | 'erledigt';
  statusChangedBy?: string;
  statusChangedAt?: string;
  notes?: Array<{ id: string; authorName: string; content: string; createdAt: string; }>;
  onAddNote?: (content: string, mentions: string[]) => void;
  onDeleteNote?: (noteId: string) => void;
  assignments?: Array<{ assignedTo: string; assignedBy: string; assignedAt: string; }>;
  onAssign?: (userId: string) => void;
  onUnassign?: (userId: string) => void;
  teamMembers?: Array<{ id: string; vorname: string; nachname: string; rolle?: string; }>;
  currentUserId?: string;
  docAI?: ReturnType<typeof useDocAI>;
  selectedConversationMessages?: any[];
}) {
  const [noteInput, setNoteInput] = useState('');
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  if (!conversation) {
    return (
      <div className="hidden xl:flex w-[360px] flex-col border-l border-gray-200 bg-white items-center justify-center flex-shrink-0">
        <UserCircle className="w-16 h-16 text-gray-300 mb-3" />
        <p className="text-base text-gray-500 font-medium">Kontaktdetails</p>
        <p className="text-sm text-gray-400 mt-1">Wähle eine Unterhaltung</p>
      </div>
    );
  }

  const cfg = CHANNEL_CONFIG[conversation.channel] || CHANNEL_CONFIG.chat;

  return (
    <div className="hidden xl:flex w-[360px] flex-col border-l border-gray-200 bg-white flex-shrink-0">
      <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2 flex-shrink-0">
        <ChevronLeft className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-bold text-gray-600 uppercase tracking-wider">Attribute</span>
      </div>
      <div className="px-5 py-5 border-b border-gray-100 text-center">
        <div
          className="rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3"
          style={{ backgroundColor: cfg.bgColor, color: cfg.color, width: '84px', height: '84px' }}
        >
          {conversation.avatarInitials || conversation.name.slice(0, 2).toUpperCase()}
        </div>
        <h3 className="text-lg font-bold text-gray-900">{conversation.name}</h3>
        {conversation.subtitle && (
          <p className="text-sm text-gray-500 mt-0.5">{conversation.subtitle}</p>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="space-y-3">
          <div className="space-y-3">
            <ContactField label="Vorname" value={conversation.name.split(' ')[0] || ''} />
            <ContactField label="Nachname" value={conversation.name.split(' ').slice(1).join(' ') || ''} />
            <ContactField label="E-Mail" value={conversation.contactEmail || ''} icon={<Mail className="w-3.5 h-3.5 text-gray-500" />} />
            <ContactField label="Telefon" value={conversation.contactPhone || ''} icon={<Phone className="w-3.5 h-3.5 text-gray-500" />} />
            <ContactField label="Firma" value={conversation.contactFirma || ''} icon={<Building2 className="w-3.5 h-3.5 text-gray-500" />} />
            <div className="border-t border-gray-100 pt-2.5 mt-2.5">
              <ContactField label="Aktenzeichen" value="" placeholder="Eingeben" />
              <ContactField label="Termin am" value="" placeholder="Auswählen" />
            </div>
          </div>
          <div className="border-t border-gray-200 pt-3 mt-3">
            <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">CRM-Verknüpfung</p>
            <button
              onClick={() => toast.info('CRM-Verknüpfung', { description: 'Diese Funktion wird in einem kommenden Update verfügbar sein.' })}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-left"
            >
              <Link2 className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">Mit CRM-Kontakt verknüpfen</span>
            </button>
            <button
              onClick={() => {
                if (conversation?.contactEmail) {
                  window.location.href = `/kunden?search=${encodeURIComponent(conversation.contactEmail)}`;
                } else {
                  toast.info('CRM öffnen', { description: 'Kein Kontakt zugeordnet — bitte zuerst verknüpfen.' });
                }
              }}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-left mt-2"
            >
              <ExternalLink className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">In CRM öffnen</span>
            </button>
          </div>
          <div className="border-t border-gray-200 pt-3 mt-3">
            <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Kanal</p>
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-gray-100">
              <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: cfg.bgColor }}>
                {(() => { const Icon = cfg.icon; return <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />; })()}
              </div>
              <span className="text-sm text-gray-700 font-semibold">{cfg.label}</span>
            </div>
          </div>

          {/* Status-Schnellwahl */}
          {onSetStatus && (
            <div className="border-t border-gray-200 pt-3 mt-3">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Status</p>
              <div className="flex gap-1.5">
                {(['offen', 'spaeter', 'erledigt'] as const).map(s => {
                  const labels = { offen: 'Offen', spaeter: 'Sp\u00e4ter', erledigt: 'Erledigt' };
                  const colors = { 
                    offen: currentStatus === 'offen' ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-blue-50',
                    spaeter: currentStatus === 'spaeter' ? 'bg-orange-100 text-orange-700 border-orange-300' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-orange-50',
                    erledigt: currentStatus === 'erledigt' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-green-50',
                  };
                  const icons = { offen: Inbox, spaeter: Clock, erledigt: CheckCircle2 };
                  const StatusIcon = icons[s];
                  return (
                    <button
                      key={s}
                      onClick={() => onSetStatus(s)}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg border text-xs font-semibold transition-colors ${colors[s]}`}
                    >
                      <StatusIcon className="w-3.5 h-3.5" />
                      {labels[s]}
                    </button>
                  );
                })}
              </div>
              {statusChangedBy && (
                <p data-testid="status-changed-by" className="text-[10px] text-gray-400 mt-1.5 flex items-center gap-1">
                  <Pencil className="w-2.5 h-2.5" />
                  Geändert von <span className="font-semibold text-gray-500">{statusChangedBy}</span>
                  {statusChangedAt && (
                    <span>· {new Date(statusChangedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                  )}
                </p>
              )}
            </div>
          )}

          {/* Mitarbeiter-Zuordnung */}
          {onAssign && (
            <div className="border-t border-gray-200 pt-3 mt-3">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Zugewiesen an</p>
              {assignments && assignments.length > 0 && (
                <div className="space-y-1.5 mb-2">
                  {assignments.map(a => {
                    const member = teamMembersList?.find(m => m.id === a.assignedTo);
                    const name = member ? `${member.vorname} ${member.nachname}` : a.assignedTo;
                    const initials = member ? `${member.vorname[0]}${member.nachname[0]}` : '??';
                    return (
                      <div key={a.assignedTo} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-blue-50 border border-blue-100">
                        <div className="w-7 h-7 rounded-full bg-blue-200 flex items-center justify-center text-xs font-bold text-blue-800">
                          {initials}
                        </div>
                        <span className="text-sm text-gray-800 font-medium flex-1">{name}</span>
                        {onUnassign && (
                          <button onClick={() => onUnassign(a.assignedTo)} className="w-5 h-5 flex items-center justify-center rounded hover:bg-red-100 text-gray-400 hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="relative">
                <button
                  onClick={() => setShowAssignDropdown(!showAssignDropdown)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-left"
                >
                  <UserPlus className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Mitarbeiter zuordnen</span>
                </button>
                {showAssignDropdown && teamMembersList && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowAssignDropdown(false)} />
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-50 max-h-48 overflow-y-auto">
                      {teamMembersList.filter(m => !assignments?.some(a => a.assignedTo === m.id)).map(m => (
                        <button
                          key={m.id}
                          onClick={() => { onAssign(m.id); setShowAssignDropdown(false); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                            {m.vorname[0]}{m.nachname[0]}
                          </div>
                          <div>
                            <span className="font-medium">{m.vorname} {m.nachname}</span>
                            {m.rolle && <span className="text-xs text-gray-400 ml-1">({m.rolle})</span>}
                          </div>
                        </button>
                      ))}
                      {teamMembersList.filter(m => !assignments?.some(a => a.assignedTo === m.id)).length === 0 && (
                        <p className="px-3 py-2 text-xs text-gray-400">Alle Mitarbeiter zugeordnet</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Notizen & Diskussion */}
          {onAddNote && (
            <div className="border-t border-gray-200 pt-3 mt-3">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Notizen & Diskussion</p>
              {notes && notes.length > 0 && (
                <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                  {notes.map(note => (
                    <div key={note.id} className="bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-gray-700">{note.authorName}</span>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-gray-400">
                            {new Date(note.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {onDeleteNote && (
                            <button onClick={() => onDeleteNote(note.id)} className="w-4 h-4 flex items-center justify-center rounded hover:bg-red-100 text-gray-400 hover:text-red-500">
                              <X className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{note.content}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-1.5">
                <input
                  value={noteInput}
                  onChange={e => setNoteInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && noteInput.trim()) {
                      // Parse @mentions
                      const mentionRegex = /@(\w+)/g;
                      const mentions: string[] = [];
                      let match;
                      while ((match = mentionRegex.exec(noteInput)) !== null) {
                        const mentioned = teamMembersList?.find(m => 
                          m.vorname.toLowerCase() === match![1].toLowerCase() ||
                          `${m.vorname}${m.nachname}`.toLowerCase() === match![1].toLowerCase()
                        );
                        if (mentioned) mentions.push(mentioned.id);
                      }
                      onAddNote(noteInput.trim(), mentions);
                      setNoteInput('');
                    }
                  }}
                  placeholder="Notiz schreiben... (@Name f\u00fcr Erw\u00e4hnung)"
                  aria-label="Notiz schreiben"
                  className="flex-1 text-xs bg-gray-100 rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#C41E3A]/30 placeholder-gray-400"
                />
                <button
                  onClick={() => {
                    if (noteInput.trim()) {
                      const mentionRegex = /@(\w+)/g;
                      const mentions: string[] = [];
                      let match;
                      while ((match = mentionRegex.exec(noteInput)) !== null) {
                        const mentioned = teamMembersList?.find(m => 
                          m.vorname.toLowerCase() === match![1].toLowerCase()
                        );
                        if (mentioned) mentions.push(mentioned.id);
                      }
                      onAddNote(noteInput.trim(), mentions);
                      setNoteInput('');
                    }
                  }}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#C41E3A] hover:bg-[#A3172E] text-white transition-colors flex-shrink-0"
                  title="Notiz senden"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </div>
                     <p className="text-[10px] text-gray-400 mt-1">Tipp: @Name für Erwähnung, Enter zum Senden</p>
            </div>
          )}

          {/* KI-Dokumentenanalyse */}
          {conversation.channel === 'email' && docAI && (
            <div className="border-t border-gray-200 pt-3 mt-3">
              <DocAIPanel
                analysis={docAI.analysis}
                analyzing={docAI.analyzing}
                onAnalyze={() => {
                  // Erste Nachricht im Thread für Analyse verwenden
                  const msgs = selectedConversationMessages || [];
                  const firstMsg = msgs[0];
                  if (firstMsg) {
                    const attachNames = (firstMsg.attachments || []).map((a: any) => a.filename);
                    docAI.analyzeEmail(
                      firstMsg.subject || conversation.subtitle || '',
                      firstMsg.from || conversation.name,
                      firstMsg.snippet || firstMsg.body || '',
                      attachNames
                    );
                  } else {
                    docAI.analyzeEmail(
                      conversation.subtitle || '',
                      conversation.name,
                      conversation.lastMessage || '',
                      []
                    );
                  }
                }}
                onDismiss={() => docAI.clearAnalysis()}
                onCreateTask={async (aktion) => {
                  try {
                    const { error } = await supabase.from('aufgaben').insert({
                      titel: aktion.titel,
                      beschreibung: `${aktion.beschreibung}\n\nErstellt aus E-Mail: ${conversation?.name} — ${conversation?.lastMessage}`,
                      prioritaet: aktion.prioritaet === 'hoch' ? 'hoch' : aktion.prioritaet === 'mittel' ? 'mittel' : 'niedrig',
                      kategorie: 'email_aufgabe',
                      status: 'offen',
                      business_unit: 'gutachten',
                      faellig_am: aktion.frist || null,
                      wiedervorlage_notiz: JSON.stringify({
                        quelle: 'ki_analyse',
                        email_betreff: conversation?.name,
                        email_von: conversation?.lastMessage,
                        typ: aktion.typ,
                        betrag: aktion.betrag,
                        iban: aktion.iban,
                        verwendungszweck: aktion.verwendungszweck,
                      }),
                    });
                    if (error) throw error;
                    toast.success('Aufgabe erstellt', { description: aktion.titel });
                  } catch (err: any) {
                    toast.error('Fehler beim Erstellen der Aufgabe', { description: err.message });
                  }
                }}
                onCreateTransfer={async (aktion) => {
                  try {
                    const { error } = await supabase.from('aufgaben').insert({
                      titel: `Überweisung: ${aktion.betrag?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} an ${aktion.empfaenger || 'Empfänger'}`,
                      beschreibung: [
                        `Betrag: ${aktion.betrag?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}`,
                        aktion.iban ? `IBAN: ${aktion.iban}` : '',
                        aktion.verwendungszweck ? `Verwendungszweck: ${aktion.verwendungszweck}` : '',
                        aktion.empfaenger ? `Empfänger: ${aktion.empfaenger}` : '',
                        `\nErstellt aus E-Mail: ${conversation?.name}`,
                      ].filter(Boolean).join('\n'),
                      prioritaet: aktion.prioritaet === 'hoch' ? 'hoch' : 'mittel',
                      kategorie: 'ueberweisung',
                      status: 'offen',
                      business_unit: 'buchhaltung',
                      faellig_am: aktion.frist || null,
                      wiedervorlage_notiz: JSON.stringify({
                        quelle: 'ki_analyse',
                        typ: 'ueberweisung',
                        betrag: aktion.betrag,
                        iban: aktion.iban,
                        bic: aktion.verwendungszweck,
                        empfaenger: aktion.empfaenger,
                        verwendungszweck: aktion.verwendungszweck,
                      }),
                    });
                    if (error) throw error;
                    toast.success('Überweisung als Aufgabe erstellt', {
                      description: `${aktion.betrag?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} an ${aktion.empfaenger || 'Empfänger'}`,
                    });
                  } catch (err: any) {
                    toast.error('Fehler beim Erstellen', { description: err.message });
                  }
                }}
                onFileToFolder={(pfad) => {
                  toast.success('Ablagevorschlag gemerkt', {
                    description: `Anhänge → ${pfad}`,
                    action: { label: 'Dokumente öffnen', onClick: () => window.location.href = '/dokumente' },
                  });
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactField({ label, value, placeholder, icon }: { 
  label: string; value: string; placeholder?: string; icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-gray-500 flex items-center gap-2">{icon}{label}</span>
      {value ? (
        <span className="text-sm text-gray-900 font-semibold text-right max-w-[150px] truncate">{value}</span>
      ) : (
        <span className="text-sm text-gray-400 italic">{placeholder || 'Eingeben'}</span>
      )}
    </div>
  );
}

// ─── Aufklappbare Anhang-Leiste (responsive, robust) ─────────────────────────

function CollapsibleAttachmentBar({ allAttachments, maxVisible, hasMore, onDownloadAttachment, onOpenAttachment }: {
  allAttachments: { filename: string; mimeType: string; attachmentId: string; size: number; messageId: string }[];
  maxVisible: number;
  hasMore: boolean;
  onDownloadAttachment?: (messageId: string, attachmentId: string, filename: string, mimeType: string) => void;
  onOpenAttachment?: (messageId: string, attachmentId: string, filename: string, mimeType: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const visibleAttachments = expanded ? allAttachments : allAttachments.slice(0, maxVisible);
  const hiddenCount = allAttachments.length - maxVisible;

  return (
    <div data-testid="attachment-bar" className="border-t border-gray-50 bg-white px-4 sm:px-5 py-2.5 flex-shrink-0">
      <button
        onClick={() => hasMore && setExpanded(!expanded)}
        className={`text-[11px] font-medium text-gray-400 mb-2 flex items-center gap-1.5 w-full text-left ${hasMore ? 'cursor-pointer hover:text-gray-600' : 'cursor-default'}`}
      >
        <Paperclip className="w-3 h-3 flex-shrink-0" />
        <span>{allAttachments.length} Anhang{allAttachments.length > 1 ? '\u00e4nge' : ''}</span>
        {hasMore && (
          <span className="ml-auto flex items-center gap-1 text-[10px] text-gray-300">
            {expanded ? 'Weniger' : `+${hiddenCount}`}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </span>
        )}
      </button>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-[180px] overflow-y-auto">
        {visibleAttachments.map((att, i) => {
          const isPreviewable = /^(application\/pdf|image\/(png|jpeg|jpg|gif|webp|svg))$/i.test(att.mimeType);
          const FileIconComp = getFileIcon(att.mimeType);
          const sizeStr = formatFileSize(att.size);
          return (
            <div key={`${att.messageId}-${att.attachmentId}-${i}`} className="flex items-center gap-2 bg-gray-50 rounded-md px-2.5 py-1.5 hover:bg-gray-100 transition-colors group">
              <FileIconComp className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <span className="text-[11px] font-medium text-gray-700 truncate block leading-tight">{att.filename}</span>
                {sizeStr && <span className="text-[9px] text-gray-400">{sizeStr}</span>}
              </div>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                {isPreviewable && onOpenAttachment && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onOpenAttachment(att.messageId, att.attachmentId, att.filename, att.mimeType); }}
                    className="w-5 h-5 flex items-center justify-center rounded hover:bg-white text-gray-400 hover:text-gray-700 transition-colors"
                    title="Vorschau"
                  >
                    <Eye className="w-3 h-3" />
                  </button>
                )}
                {onDownloadAttachment && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDownloadAttachment(att.messageId, att.attachmentId, att.filename, att.mimeType); }}
                    className="w-5 h-5 flex items-center justify-center rounded hover:bg-white text-gray-400 hover:text-gray-700 transition-colors"
                    title="Herunterladen"
                  >
                    <Download className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Einzelne E-Mail-Nachricht im Thread ─────────────────────────────────────

function EmailMessageCard({ 
  message, 
  isExpanded, 
  onToggle,
  onDownloadAttachment,
  onOpenAttachment,
}: {
  message: GmailMessage;
  isExpanded: boolean;
  onToggle: () => void;
  onDownloadAttachment?: (messageId: string, attachmentId: string, filename: string, mimeType: string) => void;
  onOpenAttachment?: (messageId: string, attachmentId: string, filename: string, mimeType: string) => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(200);

  useEffect(() => {
    if (isExpanded && iframeRef.current && message.bodyHtml) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 15px; color: #374151; line-height: 1.7; margin: 0; padding: 20px; word-break: break-word; overflow: hidden; } img { max-width: 100%; height: auto; } a { color: #C41E3A; } table { max-width: 100% !important; } blockquote { border-left: 3px solid #e5e7eb; margin: 8px 0; padding-left: 12px; color: #6b7280; }</style></head><body>${sanitizeEmailHtml(message.bodyHtml)}</body></html>`);
        doc.close();
        // Auto-resize iframe
        setTimeout(() => {
          if (iframeRef.current?.contentDocument?.body) {
            const h = iframeRef.current.contentDocument.body.scrollHeight;
            setIframeHeight(Math.max(100, Math.min(h + 32, 800)));
          }
        }, 200);
      }
    }
  }, [isExpanded, message.bodyHtml]);

  const hasAttachments = message.attachments && message.attachments.length > 0;

  return (
    <div className={`border rounded-lg bg-white transition-all ${isExpanded ? 'border-gray-200 shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}>
      {/* Collapsed Header */}
      <button onClick={onToggle} className="w-full flex items-start gap-3 px-4 py-3 text-left">
        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-[12px] font-semibold text-gray-600 flex-shrink-0">
          {getEmailInitials(extractName(message.from))}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[14px] font-semibold text-gray-900 leading-tight">{extractName(message.from)}</span>
            <span className="text-[11px] text-gray-400 tabular-nums">{formatTime(message.date)}</span>
            {hasAttachments && <Paperclip className="w-3.5 h-3.5 text-gray-300" />}
          </div>
          {!isExpanded && (
            <p className="text-[13px] text-gray-400 truncate mt-0.5 leading-tight">{message.snippet || ''}</p>
          )}
          {isExpanded && (
            <p className="text-[11px] text-gray-400 mt-0.5">
              An: {message.to ? extractName(message.to) : 'mich'}
              {message.cc && ` | Cc: ${extractName(message.cc)}`}
            </p>
          )}
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-300 flex-shrink-0 mt-0.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Body */}
          <div className="px-4">
            {message.bodyHtml ? (
              <iframe 
                ref={iframeRef} 
                className="w-full border-0 rounded" 
                sandbox="allow-same-origin" 
                title="E-Mail Inhalt" 
                style={{ height: `${Math.max(iframeHeight, 100)}px`, minHeight: '100px' }} 
              />
            ) : (
              <div className="py-3">
                <div className="text-[13px] text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                  {message.body || message.snippet || <span className="text-gray-400 italic">Kein Inhalt verf\u00fcgbar</span>}
                </div>
              </div>
            )}
          </div>

          {/* Anhänge */}
          {hasAttachments && (
            <div className="px-4 py-3 border-t border-gray-100">
              <p className="text-xs font-bold text-gray-500 mb-2 flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" />
                {message.attachments!.length} Anhang{message.attachments!.length > 1 ? 'änge' : ''}
              </p>
              <div className="flex flex-wrap gap-2">
                {message.attachments!.map((att, i) => {
                  const isPreviewable = /^(application\/pdf|image\/(png|jpeg|jpg|gif|webp|svg))$/i.test(att.mimeType);
                  const FileIconComp = getFileIcon(att.mimeType);
                  const sizeStr = att.size >= 1024 * 1024 
                    ? `${(att.size / (1024 * 1024)).toFixed(1)}MB` 
                    : `${Math.round(att.size / 1024)}KB`;
                  
                  return (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-200 hover:border-gray-300 hover:bg-gray-100 transition-colors group">
                      <FileIconComp className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-semibold text-gray-800 truncate block max-w-[180px]">{att.filename}</span>
                        <span className="text-[10px] text-gray-500">{sizeStr}</span>
                      </div>
                      <div className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
                        {isPreviewable && onOpenAttachment && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onOpenAttachment(message.id, att.attachmentId, att.filename, att.mimeType); }}
                            className="w-6 h-6 flex items-center justify-center rounded hover:bg-white text-gray-500 hover:text-[#C41E3A] transition-colors"
                            title="Öffnen"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        )}
                        {onDownloadAttachment && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onDownloadAttachment(message.id, att.attachmentId, att.filename, att.mimeType); }}
                            className="w-6 h-6 flex items-center justify-center rounded hover:bg-white text-gray-500 hover:text-[#C41E3A] transition-colors"
                            title="Herunterladen"
                          >
                            <Download className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Inline Notizen-Bereich (prominent im E-Mail-Detail) ──────────────────────

function InlineNotesSection({
  notes,
  onAddNote,
  onDeleteNote,
  currentUserName,
}: {
  notes: Array<{ id: string; authorName: string; content: string; createdAt: string }>;
  onAddNote: (content: string) => void;
  onDeleteNote?: (noteId: string) => void;
  currentUserName?: string;
}) {
  const [noteInput, setNoteInput] = useState('');

  return (
    <div data-testid="conversation-notes" className="border-t border-gray-100 bg-amber-50/30 px-4 sm:px-5 py-2.5 flex-shrink-0">
      <div className="flex items-center gap-1.5 mb-2">
        <MessageSquare className="w-3.5 h-3.5 text-amber-500" />
        <span className="text-[11px] font-medium text-gray-500">Notizen</span>
        {notes.length > 0 && (
          <span className="text-[10px] text-amber-600 font-medium">{notes.length}</span>
        )}
      </div>
      {notes.length > 0 && (
        <div className="space-y-1.5 mb-2 max-h-32 overflow-y-auto">
          {notes.map(note => (
            <div key={note.id} className="bg-white border border-amber-100/50 rounded-md px-3 py-1.5 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-700">{note.authorName}</span>
                  <span className="text-[10px] text-gray-400">
                    {new Date(note.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-0.5">{note.content}</p>
              </div>
              {onDeleteNote && (
                <button onClick={() => onDeleteNote(note.id)} className="w-5 h-5 flex items-center justify-center rounded hover:bg-red-100 text-gray-400 hover:text-red-500 flex-shrink-0 mt-0.5">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-1.5">
        <input
          value={noteInput}
          onChange={e => setNoteInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && noteInput.trim()) {
              onAddNote(noteInput.trim());
              setNoteInput('');
            }
          }}
          placeholder="Notiz schreiben..."
          className="flex-1 text-[12px] bg-white rounded-md px-3 py-2 outline-none focus:ring-1 focus:ring-amber-200 border border-gray-200 placeholder-gray-400"
        />
        <button
          onClick={() => {
            if (noteInput.trim()) {
              onAddNote(noteInput.trim());
              setNoteInput('');
            }
          }}
          className="w-8 h-8 flex items-center justify-center rounded-md bg-amber-500 hover:bg-amber-600 text-white transition-colors flex-shrink-0"
          title="Notiz senden"
        >
          <Send className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ─── E-Mail Thread-Ansicht (in der Mitte) ─────────────────────────────────────

function EmailThreadView({
  messages,
  threadLoading,
  subject,
  onBack,
  onReply,
  onForward,
  onMarkRead,
  onMarkUnread,
  onTrash,
  onArchive,
  onSpam,
  onDownloadAttachment,
  onOpenAttachment,
  onCreateTask,
  onSetStatus,
  currentStatus,
  notes,
  onAddNote,
  onDeleteNote,
  currentUserName,
}: {
  messages: GmailMessage[];
  threadLoading: boolean;
  subject: string;
  onBack: () => void;
  onReply: (msg: GmailMessage) => void;
  onForward: (msg: GmailMessage) => void;
  onMarkRead: (msg: GmailMessage) => void;
  onMarkUnread: (msg: GmailMessage) => void;
  onTrash: (msg: GmailMessage) => void;
  onArchive: (msg: GmailMessage) => void;
  onSpam?: (msg: GmailMessage) => void;
  onDownloadAttachment?: (messageId: string, attachmentId: string, filename: string, mimeType: string) => void;
  onOpenAttachment?: (messageId: string, attachmentId: string, filename: string, mimeType: string) => void;
  onCreateTask?: (msg: GmailMessage) => void;
  onSetStatus?: (status: 'offen' | 'spaeter' | 'erledigt') => void;
  currentStatus?: 'offen' | 'spaeter' | 'erledigt';
  notes?: Array<{ id: string; authorName: string; content: string; createdAt: string }>;
  onAddNote?: (content: string) => void;
  onDeleteNote?: (noteId: string) => void;
  currentUserName?: string;
}) {
  const [showActions, setShowActions] = useState(false);
  // Letzte Nachricht ist standardmäßig aufgeklappt
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  const lastMessage = messages[messages.length - 1];

  // Keyboard Shortcuts für schnelles Arbeiten
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Nicht auslösen wenn in Input/Textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (!lastMessage || !onSetStatus) return;
      switch(e.key.toLowerCase()) {
        case 'e': onSetStatus('erledigt'); break;
        case 's': if (!e.ctrlKey && !e.metaKey) onSetStatus('spaeter'); break;
        case 'a': onArchive(lastMessage); break;
        case 'r': if (!e.ctrlKey && !e.metaKey) onReply(lastMessage); break;
        case '#': case 'delete': onTrash(lastMessage); break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lastMessage, onSetStatus, onArchive, onTrash, onReply]);

  // Letzte Nachricht automatisch aufklappen
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      setExpandedIds(new Set([lastMsg.id]));
      // Zum Ende scrollen
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
      }, 100);
    }
  }, [messages]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex-1 flex flex-col bg-white h-full min-w-0">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-3 sm:px-5 py-3 border-b border-gray-100 bg-white flex-shrink-0">
        <button onClick={onBack} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors" aria-label="Zur\u00fcck zur \u00dcbersicht">
          <ArrowLeft className="w-4 h-4 text-gray-500" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-[15px] font-semibold text-gray-900 truncate leading-tight">{subject}</h3>
            {currentStatus && currentStatus !== 'offen' && (
              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0 ${
                currentStatus === 'spaeter' ? 'bg-orange-50 text-orange-600' : 'bg-green-50 text-green-600'
              }`}>
                {currentStatus === 'spaeter' ? 'Sp\u00e4ter' : 'Erledigt'}
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-400 mt-0.5">{messages.length} Nachricht{messages.length > 1 ? 'en' : ''}
            <span className="hidden sm:inline text-gray-300 ml-1.5">\u2022 E=Erledigt S=Sp\u00e4ter R=Antworten</span>
          </p>
        </div>
        <div className="flex items-center gap-1">
          {/* Status-Buttons */}
          {onSetStatus && (
            <>
              {currentStatus !== 'spaeter' && (
                <button 
                  onClick={() => onSetStatus('spaeter')} 
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-orange-50 transition-colors" 
                  title="Später bearbeiten"
                >
                  <Clock className="w-4 h-4 text-orange-500" />
                </button>
              )}
              {currentStatus !== 'erledigt' && (
                <button 
                  onClick={() => onSetStatus('erledigt')} 
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-green-50 transition-colors" 
                  title="Als erledigt markieren"
                >
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                </button>
              )}
              {currentStatus !== 'offen' && (
                <button 
                  onClick={() => onSetStatus('offen')} 
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-blue-50 transition-colors" 
                  title="Zurück zu Offen"
                >
                  <Inbox className="w-4 h-4 text-blue-500" />
                </button>
              )}
              <div className="w-px h-5 bg-gray-200 mx-0.5" />
            </>
          )}
          <button onClick={() => lastMessage && onCreateTask?.(lastMessage)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50" title="Aufgabe erstellen">
            <ListTodo className="w-4 h-4 text-amber-600" />
          </button>
          <button onClick={() => lastMessage && onArchive(lastMessage)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100" title="Archivieren — E-Mail bleibt erhalten, verschwindet aus Posteingang">
            <Archive className="w-4 h-4 text-gray-500" />
          </button>
          <button onClick={() => lastMessage && onTrash(lastMessage)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50" title="In Papierkorb verschieben — E-Mail wird nach 30 Tagen gelöscht">
            <Trash2 className="w-4 h-4 text-red-400" />
          </button>
          <div className="relative">
            <button onClick={() => setShowActions(!showActions)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
              <MoreVertical className="w-4 h-4 text-gray-500" />
            </button>
            {showActions && (
              <>
              <div className="fixed inset-0 z-[5]" onClick={() => setShowActions(false)} />
              <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-10 py-1">
                <button onClick={() => { lastMessage && onMarkRead(lastMessage); setShowActions(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                  <Eye className="w-4 h-4" /> Als gelesen markieren
                </button>
                <button onClick={() => { lastMessage && onMarkUnread(lastMessage); setShowActions(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                  <EyeOff className="w-4 h-4" /> Als ungelesen markieren
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button onClick={() => { lastMessage && onReply(lastMessage); setShowActions(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                  <Reply className="w-4 h-4" /> Antworten <span className="ml-auto text-xs text-gray-400">R</span>
                </button>
                <button onClick={() => { lastMessage && onForward(lastMessage); setShowActions(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                  <Forward className="w-4 h-4" /> Weiterleiten
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button onClick={() => { lastMessage && onCreateTask?.(lastMessage); setShowActions(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50">
                  <ListTodo className="w-4 h-4" /> Aufgabe erstellen
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button onClick={() => { lastMessage && onArchive(lastMessage); setShowActions(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                  <Archive className="w-4 h-4" /> Archivieren <span className="ml-auto text-xs text-gray-400">A</span>
                </button>
                <button onClick={() => { lastMessage && onTrash(lastMessage); setShowActions(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" /> In Papierkorb <span className="ml-auto text-xs text-gray-400">#</span>
                </button>
                {onSpam && (
                  <button onClick={() => { lastMessage && onSpam(lastMessage); setShowActions(false); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-orange-600 hover:bg-orange-50">
                    <AlertTriangle className="w-4 h-4" /> Als Spam markieren
                  </button>
                )}
              </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Thread-Nachrichten */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-5 py-4 space-y-2.5 bg-gray-50/50">
        {threadLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
            <span className="text-sm text-gray-400 ml-2">Thread wird geladen...</span>
          </div>
        ) : (
          messages.map((msg) => (
            <EmailMessageCard
              key={msg.id}
              message={msg}
              isExpanded={expandedIds.has(msg.id)}
              onToggle={() => toggleExpand(msg.id)}
              onDownloadAttachment={onDownloadAttachment}
              onOpenAttachment={onOpenAttachment}
            />
          ))
        )}
      </div>

      {/* Anhang-Leiste — alle Anhänge des Threads zusammengefasst */}
      {(() => {
        const allAttachments = messages.flatMap(msg =>
          (msg.attachments || []).map(att => ({ ...att, messageId: msg.id }))
        );
        if (allAttachments.length === 0) return null;
        const MAX_VISIBLE = 4;
        const hasMore = allAttachments.length > MAX_VISIBLE;
        return (
          <CollapsibleAttachmentBar
            allAttachments={allAttachments}
            maxVisible={MAX_VISIBLE}
            hasMore={hasMore}
            onDownloadAttachment={onDownloadAttachment}
            onOpenAttachment={onOpenAttachment}
          />
        );
      })()}

      {/* Interne Notizen & Diskussion — prominent im Detail */}
      {onAddNote && (
        <InlineNotesSection
          notes={notes || []}
          onAddNote={onAddNote}
          onDeleteNote={onDeleteNote}
          currentUserName={currentUserName}
        />
      )}

      {/* Aktions-Footer — sticky über Bottom-Nav auf Mobile */}
      <div className="border-t border-gray-100 bg-white px-4 sm:px-5 py-2.5 flex items-center gap-2 flex-shrink-0 mobile-dialog-actions sm:pb-2.5">
        <button onClick={() => lastMessage && onReply(lastMessage)} className="flex items-center gap-1.5 bg-[#C41E3A] text-white text-[13px] font-semibold px-4 py-2 rounded-lg hover:bg-[#A3172E] transition-colors min-h-[36px] flex-1 sm:flex-none justify-center">
          <Reply className="w-3.5 h-3.5" /> Antworten
        </button>
        <button onClick={() => lastMessage && onForward(lastMessage)} className="flex items-center gap-1.5 bg-gray-50 text-gray-700 text-[13px] font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors min-h-[36px] border border-gray-200">
          <Forward className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Weiterleiten</span>
        </button>
        <button onClick={() => lastMessage && onCreateTask?.(lastMessage)} className="flex items-center gap-1.5 text-gray-500 text-[13px] font-medium px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors min-h-[36px] ml-auto">
          <ListTodo className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Aufgabe</span>
        </button>
      </div>
    </div>
  );
}

// ─── E-Mail Compose Dialog ────────────────────────────────────────────────

// E-Mail-Signatur für Gutachter Koop
function getEmailSignature(senderName?: string, customSignatur?: string | null): string {
  // Wenn der Mitarbeiter eine eigene Signatur hat, diese verwenden
  if (customSignatur && customSignatur.trim()) {
    return `\n\n--\n${customSignatur}`;
  }
  // Fallback: Standard-Signatur
  const name = senderName || 'Gutachter Koop Team';
  return `\n\n--\n${name}\nGutachter Koop\nSachverständigenbüro für Kfz-Schäden\n\nTel: +49 (0) 40 - 123 456 78\nE-Mail: info@gutachter-koop.de\nWeb: www.gutachter-koop.de`;
}

function getEmailSignatureHtml(senderName?: string, customSignatur?: string | null, email?: string, telefon?: string, position?: string, accountEmail?: string): string {
  // MietGo-Signatur wenn das Konto info@mietgo.de ist
  if (accountEmail === 'info@mietgo.de') {
    return getMietGoSignatureHtml();
  }
  // Lenneglas-Signatur wenn das Konto info@lenneglas.de ist
  if (accountEmail === 'info@lenneglas.de') {
    return getLenneglasSignatureHtml(senderName);
  }
  const name = senderName || 'Gutachter Koop Team';
  // Technische Rollen in professionelle Titel für die Signatur umwandeln
  const rollenMap: Record<string, string> = {
    'admin': 'Kfz-Techniker-Meister & Kfz-Sachverständiger',
    'sachbearbeiter': 'Sachbearbeitung',
    'mitarbeiter': 'Kfz-Sachverständigenbüro',
    'ki_agent': 'Kfz-Sachverständigenbüro',
    'gutachter': 'Kfz-Sachverständiger',
  };
  const pos = position ? (rollenMap[position.toLowerCase()] || position) : 'Kfz-Sachverständigenbüro';
  const tel = telefon || '0170 500 4022';
  const mail = email || 'info@gutachter-koop.de';
  
  // Koop-Dunkelblau aus dem Logo
  const kBlau = '#1B3A5C';
  
  return `<br><br>
<table cellpadding="0" cellspacing="0" border="0" style="font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 420px; border-collapse: collapse;">
  <tr>
    <td style="padding: 0 0 10px 0;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="border-top: 2px solid ${kBlau}; font-size: 1px; line-height: 1px;">&nbsp;</td></tr></table>
    </td>
  </tr>
  <tr>
    <td style="vertical-align: middle; padding: 0 0 8px 0;">
      <table cellpadding="0" cellspacing="0" border="0"><tr>
        <td style="vertical-align: middle; padding: 0 12px 0 0;">
          <img src="https://app.gutachter-koop.de/logo-dark.png" alt="KOOP" width="64" style="display: block; width: 64px; height: auto; border: 0;" />
        </td>
        <td style="vertical-align: middle; padding: 0;">
          <span style="font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 14px; font-weight: 700; color: #1A1A2E; line-height: 1.2;">${name}</span><br>
          <span style="font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 9px; font-weight: 600; color: ${kBlau}; letter-spacing: 0.8px; text-transform: uppercase; line-height: 1.3;">${pos}</span>
        </td>
      </tr></table>
    </td>
  </tr>
  <tr>
    <td style="padding: 0 0 8px 0;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="border-top: 1px solid #E5E7EB; font-size: 1px; line-height: 1px;">&nbsp;</td></tr></table>
    </td>
  </tr>
  <tr>
    <td style="padding: 0 0 8px 0;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td style="padding: 0 14px 0 0; vertical-align: middle;">
            <table cellpadding="0" cellspacing="0" border="0"><tr>
              <td style="vertical-align: middle; padding: 0 4px 0 0;"><img src="https://app.gutachter-koop.de/icons/phone-mobile.png" alt="" width="12" height="12" style="display: block; border: 0;" /></td>
              <td style="font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: #374151; vertical-align: middle;"><a href="tel:${tel.replace(/\s/g, '')}" style="color: #374151; text-decoration: none;">${tel}</a></td>
            </tr></table>
          </td>
          <td style="padding: 0 14px 0 0; vertical-align: middle;">
            <table cellpadding="0" cellspacing="0" border="0"><tr>
              <td style="vertical-align: middle; padding: 0 4px 0 0;"><img src="https://app.gutachter-koop.de/icons/phone-office.png" alt="" width="12" height="12" style="display: block; border: 0;" /></td>
              <td style="font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: #374151; vertical-align: middle;"><a href="tel:+4923519265740" style="color: #374151; text-decoration: none;">02351 926 5740</a></td>
            </tr></table>
          </td>
        </tr>
        <tr>
          <td style="padding: 4px 14px 0 0; vertical-align: middle;">
            <table cellpadding="0" cellspacing="0" border="0"><tr>
              <td style="vertical-align: middle; padding: 0 4px 0 0;"><img src="https://app.gutachter-koop.de/icons/mail.png" alt="" width="12" height="12" style="display: block; border: 0;" /></td>
              <td style="font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: ${kBlau}; vertical-align: middle;"><a href="mailto:${mail}" style="color: ${kBlau}; text-decoration: none;">${mail}</a></td>
            </tr></table>
          </td>
          <td style="padding: 4px 14px 0 0; vertical-align: middle;">
            <table cellpadding="0" cellspacing="0" border="0"><tr>
              <td style="vertical-align: middle; padding: 0 4px 0 0;"><img src="https://app.gutachter-koop.de/icons/globe.png" alt="" width="12" height="12" style="display: block; border: 0;" /></td>
              <td style="font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: ${kBlau}; vertical-align: middle;"><a href="https://gutachter-koop.de" style="color: ${kBlau}; text-decoration: none;">gutachter-koop.de</a></td>
            </tr></table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding: 0;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="border-top: 1px solid #E5E7EB; font-size: 1px; line-height: 1px;">&nbsp;</td></tr></table>
    </td>
  </tr>
  <tr>
    <td style="padding: 6px 0 0 0;">
      <span style="font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 9px; color: #9CA3AF; line-height: 1.4;"><strong style="color: #6B7280;">SV Büro Koop</strong> · Rahmedestraße 65 · 58507 Lüdenscheid</span>
    </td>
  </tr>
  <tr>
    <td style="padding: 6px 0 0 0;">
      <span style="font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 8px; color: #B0B5BD; line-height: 1.4;">Diese E-Mail ist vertraulich und ausschließlich für den Adressaten bestimmt.</span>
    </td>
  </tr>
</table>`;
}

function ComposeEmailDialog({ onClose, onSend, replyTo, forwardMsg, connectedEmail, emailAccounts, selectedAccountId, onSelectAccount }: {
  onClose: () => void;
  onSend: (to: string, subject: string, body: string, cc?: string, bcc?: string, inReplyTo?: string, references?: string, attachments?: EmailAttachment[]) => Promise<any>;
  replyTo?: GmailMessage | null;
  forwardMsg?: GmailMessage | null;
  connectedEmail?: string | null;
  emailAccounts?: any[];
  selectedAccountId?: string | null;
  onSelectAccount?: (id: string) => void;
}) {
  const { profile, teamMembers } = useAuth();
  // Mitarbeiter-Auswahl: Wer sendet die E-Mail?
  const [selectedMitarbeiterId, setSelectedMitarbeiterId] = useState<string | null>(null);
  const selectedMitarbeiter = selectedMitarbeiterId ? teamMembers.find(m => m.id === selectedMitarbeiterId) : null;
  const senderName = selectedMitarbeiter
    ? `${selectedMitarbeiter.vorname} ${selectedMitarbeiter.nachname}`.trim()
    : profile ? `${profile.vorname || ''} ${profile.nachname || ''}`.trim() : undefined;
  const senderSignatur = selectedMitarbeiter?.email_signatur || profile?.email_signatur;
  const senderTelefon = selectedMitarbeiter?.telefon || profile?.telefon || undefined;
  const senderRolle = selectedMitarbeiter?.rolle || profile?.rolle || undefined;

  // Mobile Back-Button: Dialog schließen statt Seite wechseln
  useMobileBackHandler(true, onClose);

  const [to, setTo] = useState(replyTo ? extractEmail(replyTo.from) : '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(
    replyTo ? `Re: ${replyTo.subject}` :
    forwardMsg ? `Fwd: ${forwardMsg.subject}` : ''
  );
  const [body, setBody] = useState(
    forwardMsg 
      ? `\n\n--- Weitergeleitete Nachricht ---\nVon: ${forwardMsg.from}\nDatum: ${forwardMsg.date}\nBetreff: ${forwardMsg.subject}\n\n${forwardMsg.body || forwardMsg.snippet || ''}` 
      : ''
  );
  const [bcc, setBcc] = useState('');
  const [sending, setSending] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  // Kontakt-Autovervollständigung
  const { search: searchKontakte } = useCRMKontaktSuche();
  const [toSuggestions, setToSuggestions] = useState<CRMKontakt[]>([]);
  const [ccSuggestions, setCcSuggestions] = useState<CRMKontakt[]>([]);
  const [bccSuggestions, setBccSuggestions] = useState<CRMKontakt[]>([]);
  const [activeField, setActiveField] = useState<'to' | 'cc' | 'bcc' | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const handleEmailFieldChange = async (value: string, setter: (v: string) => void, sugSetter: (v: CRMKontakt[]) => void, field: 'to' | 'cc' | 'bcc') => {
    setter(value);
    setActiveField(field);
    // Nur den letzten Teil nach Komma suchen (für mehrere Empfänger)
    const parts = value.split(',');
    const lastPart = parts[parts.length - 1].trim();
    if (lastPart.length >= 2) {
      const results = await searchKontakte(lastPart);
      sugSetter(results.filter(k => k.email));
    } else {
      sugSetter([]);
    }
  };

  const selectSuggestion = (kontakt: CRMKontakt, field: 'to' | 'cc' | 'bcc') => {
    const setter = field === 'to' ? setTo : field === 'cc' ? setCc : setBcc;
    const sugSetter = field === 'to' ? setToSuggestions : field === 'cc' ? setCcSuggestions : setBccSuggestions;
    const currentVal = field === 'to' ? to : field === 'cc' ? cc : bcc;
    const parts = currentVal.split(',').map(p => p.trim()).filter(Boolean);
    parts.pop(); // Letzten (unvollständigen) Teil entfernen
    const emailEntry = kontakt.name ? `${kontakt.name} <${kontakt.email}>` : kontakt.email!;
    parts.push(emailEntry);
    setter(parts.join(', '));
    sugSetter([]);
    setActiveField(null);
  };
  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [priority, setPriority] = useState<'normal' | 'high'>('normal');
  const [readReceipt, setReadReceipt] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Datei zu Base64 konvertieren
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // data:...;base64,XXXX -> nur den base64-Teil
        const base64 = result.split(',')[1] || '';
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleAddAttachments = async (files: FileList) => {
    const newAttachments: EmailAttachment[] = [];
    for (const file of Array.from(files)) {
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`${file.name} ist zu groß (max. 25 MB)`);
        continue;
      }
      try {
        const base64Data = await fileToBase64(file);
        newAttachments.push({ filename: file.name, mimeType: file.type || 'application/octet-stream', base64Data });
      } catch {
        toast.error(`Fehler beim Laden von ${file.name}`);
      }
    }
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getByteSize = (base64: string) => Math.ceil(base64.length * 0.75);

  const formatFileSize = (base64: string) => {
    const bytes = getByteSize(base64);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const totalAttachmentSize = attachments.reduce((sum, att) => sum + getByteSize(att.base64Data), 0);
  const totalSizeMB = (totalAttachmentSize / (1024 * 1024)).toFixed(1);
  const isOverLimit = totalAttachmentSize > 25 * 1024 * 1024;

  // Datei-Icon basierend auf MIME-Type
  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return ImageIcon;
    if (mimeType.startsWith('video/')) return Video;
    if (mimeType.startsWith('audio/')) return Music;
    if (mimeType.includes('pdf')) return FileText;
    return File;
  };

  const handleSend = async (scheduled = false) => {
    if (!to.trim()) { toast.error('Bitte Empfänger angeben'); return; }
    if (isOverLimit) { toast.error('Anhänge überschreiten 25 MB Limit'); return; }
    if (scheduled && (!scheduleDate || !scheduleTime)) {
      toast.error('Bitte Datum und Uhrzeit für den Versand angeben');
      return;
    }
    setSending(true);
    try {
      const bodyHtml = body.replace(/\n/g, '<br>').replace(/  /g, '&nbsp;&nbsp;');
      // Aktives Konto bestimmen für korrekte Signatur
      const activeAccount = emailAccounts?.find((a: any) => a.id === selectedAccountId);
      const activeAccountEmail = activeAccount?.email || connectedEmail || undefined;
      const signatureHtml = getEmailSignatureHtml(
        senderName,
        senderSignatur,
        activeAccountEmail,
        senderTelefon,
        senderRolle,
        activeAccountEmail
      );
      const fullHtml = `<div style="font-family: Arial, Helvetica, sans-serif; font-size: 14px; color: #1a1a1a; line-height: 1.6;">${bodyHtml}</div>${signatureHtml}`;

      if (scheduled) {
        // Später senden: Im localStorage speichern + Timer setzen
        const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}`);
        const now = new Date();
        const delayMs = scheduledAt.getTime() - now.getTime();
        if (delayMs < 0) {
          toast.error('Der geplante Zeitpunkt liegt in der Vergangenheit');
          setSending(false);
          return;
        }
        // E-Mail-Daten im localStorage speichern
        const scheduledEmail = {
          id: `sched_${Date.now()}`,
          to, cc: cc || null, bcc: bcc || null, subject,
          bodyHtml: fullHtml,
          attachments: attachments.length > 0 ? attachments : null,
          scheduledAt: scheduledAt.toISOString(),
          createdAt: now.toISOString(),
        };
        const existing = JSON.parse(localStorage.getItem('gutachter_koop_scheduled_emails') || '[]');
        existing.push(scheduledEmail);
        localStorage.setItem('gutachter_koop_scheduled_emails', JSON.stringify(existing));

        // Timer setzen (funktioniert solange der Browser-Tab offen ist)
        setTimeout(async () => {
          try {
            await onSend(scheduledEmail.to, scheduledEmail.subject, scheduledEmail.bodyHtml,
              scheduledEmail.cc || undefined, scheduledEmail.bcc || undefined, undefined, undefined,
              scheduledEmail.attachments || undefined);
            toast.success('Geplante E-Mail gesendet', { description: `An ${scheduledEmail.to}` });
            // Aus localStorage entfernen
            const stored = JSON.parse(localStorage.getItem('gutachter_koop_scheduled_emails') || '[]');
            const updated = stored.filter((e: any) => e.id !== scheduledEmail.id);
            localStorage.setItem('gutachter_koop_scheduled_emails', JSON.stringify(updated));
          } catch (err: any) {
            toast.error(`Geplante E-Mail fehlgeschlagen: ${err.message}`);
          }
        }, delayMs);

        toast.success('E-Mail geplant', {
          description: `Wird am ${scheduledAt.toLocaleDateString('de-DE')} um ${scheduledAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} gesendet (Browser muss geöffnet bleiben)`
        });
      } else {
        await onSend(to, subject, fullHtml, cc || undefined, bcc || undefined, undefined, undefined, attachments.length > 0 ? attachments : undefined);
        toast.success('E-Mail gesendet');
      }
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Senden');
    }
    setSending(false);
  };

  // Prevent body scroll when dialog is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label={replyTo ? 'E-Mail beantworten' : forwardMsg ? 'E-Mail weiterleiten' : 'Neue E-Mail verfassen'}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />

      {/* Dialog: Fullscreen auf Mobile, zentriert auf Desktop */}
      <div className="relative bg-white w-full sm:max-w-3xl sm:rounded-2xl sm:max-h-[90vh] flex flex-col shadow-2xl
        max-sm:fixed max-sm:inset-0 max-sm:rounded-none max-sm:z-[111]">

        {/* Header — fest oben */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0 safe-area-top">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 sm:hidden">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <h3 className="text-base font-bold text-gray-900">
              {replyTo ? 'Antworten' : forwardMsg ? 'Weiterleiten' : 'Neue E-Mail'}
            </h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 hidden sm:flex">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Scrollbarer Inhaltsbereich */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="p-4 space-y-2.5">
            {/* Von — Multi-Account Auswahl */}
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="font-medium w-8">Von:</span>
              {emailAccounts && emailAccounts.length > 1 && onSelectAccount ? (
                <EmailAccountSelector
                  accounts={emailAccounts}
                  selectedAccountId={selectedAccountId || null}
                  onSelect={onSelectAccount}
                  compact={true}
                />
              ) : (
                <span className="font-semibold text-gray-800 text-sm">
                  {emailAccounts?.find((a: any) => a.id === selectedAccountId)?.email || connectedEmail || 'Nicht verbunden'}
                </span>
              )}
              {/* Mitarbeiter-Auswahl */}
              {teamMembers.length > 1 && (
                <select
                  value={selectedMitarbeiterId || ''}
                  onChange={(e) => setSelectedMitarbeiterId(e.target.value || null)}
                  className="ml-auto text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:border-[#C41E3A]/40 outline-none"
                  title="Absender-Name f\u00fcr Signatur"
                >
                  <option value="">{profile ? `${profile.vorname} ${profile.nachname}` : 'Ich'}</option>
                  {teamMembers.filter(m => m.ist_aktiv && m.id !== profile?.id).map(m => (
                    <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>
                  ))}
                </select>
              )}
            </div>

            {/* An + Cc/Bcc Toggle — mit Kontakt-Autovervollständigung */}
            <div className="relative">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 w-8 font-medium">An:</span>
                <input value={to}
                  onChange={e => handleEmailFieldChange(e.target.value, setTo, setToSuggestions, 'to')}
                  onFocus={() => setActiveField('to')}
                  onBlur={() => setTimeout(() => { if (activeField === 'to') setToSuggestions([]); }, 200)}
                  placeholder="Name oder E-Mail eingeben..."
                  className="flex-1 text-sm outline-none bg-gray-50 rounded-lg px-3 py-2.5 text-gray-800 placeholder-gray-400 border border-gray-200 focus:border-[#C41E3A]/40 transition-colors" />
                <div className="flex gap-1 flex-shrink-0">
                  {!showCc && (
                    <button onClick={() => setShowCc(true)} className="text-xs text-[#C41E3A] font-semibold hover:underline px-1.5">Cc</button>
                  )}
                  {!showBcc && (
                    <button onClick={() => setShowBcc(true)} className="text-xs text-[#C41E3A] font-semibold hover:underline px-1.5">Bcc</button>
                  )}
                </div>
              </div>
              {/* Kontakt-Vorschläge Dropdown */}
              {toSuggestions.length > 0 && activeField === 'to' && (
                <div ref={suggestionsRef} className="absolute left-8 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                  {toSuggestions.map(k => (
                    <button key={k.id} onClick={() => selectSuggestion(k, 'to')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left transition-colors">
                      <div className="w-8 h-8 rounded-full bg-[#1B3A5C] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {k.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{k.name}</p>
                        <p className="text-xs text-gray-500 truncate">{k.email}{k.firma ? ` • ${k.firma}` : ''}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CC mit Autovervollständigung */}
            {showCc && (
              <div className="relative">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 w-8 font-medium">Cc:</span>
                  <input value={cc}
                    onChange={e => handleEmailFieldChange(e.target.value, setCc, setCcSuggestions, 'cc')}
                    onFocus={() => setActiveField('cc')}
                    onBlur={() => setTimeout(() => { if (activeField === 'cc') setCcSuggestions([]); }, 200)}
                    placeholder="Name oder E-Mail..."
                    className="flex-1 text-sm outline-none bg-gray-50 rounded-lg px-3 py-2.5 text-gray-800 placeholder-gray-400 border border-gray-200 focus:border-[#C41E3A]/40 transition-colors" />
                  <button onClick={() => { setShowCc(false); setCc(''); }} className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 flex-shrink-0">
                    <X className="w-3 h-3 text-gray-400" />
                  </button>
                </div>
                {ccSuggestions.length > 0 && activeField === 'cc' && (
                  <div className="absolute left-8 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                    {ccSuggestions.map(k => (
                      <button key={k.id} onClick={() => selectSuggestion(k, 'cc')}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left transition-colors">
                        <div className="w-8 h-8 rounded-full bg-[#1B3A5C] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {k.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{k.name}</p>
                          <p className="text-xs text-gray-500 truncate">{k.email}{k.firma ? ` • ${k.firma}` : ''}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* BCC mit Autovervollständigung */}
            {showBcc && (
              <div className="relative">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 w-8 font-medium">Bcc:</span>
                  <input value={bcc}
                    onChange={e => handleEmailFieldChange(e.target.value, setBcc, setBccSuggestions, 'bcc')}
                    onFocus={() => setActiveField('bcc')}
                    onBlur={() => setTimeout(() => { if (activeField === 'bcc') setBccSuggestions([]); }, 200)}
                    placeholder="Name oder E-Mail (Blindkopie)..."
                    className="flex-1 text-sm outline-none bg-gray-50 rounded-lg px-3 py-2.5 text-gray-800 placeholder-gray-400 border border-gray-200 focus:border-[#C41E3A]/40 transition-colors" />
                  <button onClick={() => { setShowBcc(false); setBcc(''); }} className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 flex-shrink-0">
                    <X className="w-3 h-3 text-gray-400" />
                  </button>
                </div>
                {bccSuggestions.length > 0 && activeField === 'bcc' && (
                  <div className="absolute left-8 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                    {bccSuggestions.map(k => (
                      <button key={k.id} onClick={() => selectSuggestion(k, 'bcc')}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left transition-colors">
                        <div className="w-8 h-8 rounded-full bg-[#1B3A5C] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {k.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{k.name}</p>
                          <p className="text-xs text-gray-500 truncate">{k.email}{k.firma ? ` • ${k.firma}` : ''}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Betreff */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 w-8 font-medium">Betr:</span>
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Betreff"
                className="flex-1 text-sm outline-none bg-gray-50 rounded-lg px-3 py-2.5 text-gray-800 placeholder-gray-400 border border-gray-200 focus:border-[#C41E3A]/40 transition-colors" />
              {priority === 'high' && (
                <span className="text-[10px] font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded flex-shrink-0">WICHTIG</span>
              )}
            </div>

            {/* Nachrichtenfeld */}
            <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Nachricht schreiben..."
              className="w-full text-[15px] outline-none bg-gray-50 rounded-lg px-3 py-3 min-h-[140px] sm:min-h-[260px] resize-none text-gray-800 placeholder-gray-400 border border-gray-200 focus:border-[#C41E3A]/40 transition-colors" />

            {/* Anhänge-Liste */}
            {attachments.length > 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Anhänge ({attachments.length})</p>
                  <p className={`text-[10px] font-medium ${isOverLimit ? 'text-red-500' : 'text-gray-400'}`}>
                    {totalSizeMB} / 25 MB
                  </p>
                </div>
                {isOverLimit && (
                  <div className="flex items-center gap-1.5 text-xs text-red-500 bg-red-50 rounded-lg px-3 py-1.5">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    <span>Anhänge überschreiten das 25 MB Limit. Bitte Dateien entfernen.</span>
                  </div>
                )}
                {attachments.map((att, i) => {
                  const FileIcon = getFileIcon(att.mimeType);
                  return (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                      <FileIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-xs text-gray-700 font-medium truncate flex-1">{att.filename}</span>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">{formatFileSize(att.base64Data)}</span>
                      <button onClick={() => removeAttachment(i)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 flex-shrink-0">
                        <X className="w-3 h-3 text-gray-400" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Später-Senden Planer */}
            {showSchedule && (
              <div className="bg-amber-50 rounded-xl p-3 border border-amber-200 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-amber-800 flex items-center gap-1.5">
                    <CalendarClock className="w-3.5 h-3.5" /> Später senden
                  </p>
                  <button onClick={() => setShowSchedule(false)} className="w-5 h-5 flex items-center justify-center rounded hover:bg-amber-200">
                    <X className="w-3 h-3 text-amber-600" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <input type="date" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="flex-1 text-sm bg-white rounded-lg px-3 py-2 border border-amber-200 outline-none focus:border-amber-400 text-gray-800" />
                  <input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
                    className="w-28 text-sm bg-white rounded-lg px-3 py-2 border border-amber-200 outline-none focus:border-amber-400 text-gray-800" />
                </div>
              </div>
            )}

            {/* Optionen-Dropdown (Priorität, Lesebestätigung) */}
            {showOptions && (
              <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                    <SlidersHorizontal className="w-3.5 h-3.5" /> E-Mail-Optionen
                  </p>
                  <button onClick={() => setShowOptions(false)} className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-200">
                    <X className="w-3 h-3 text-gray-500" />
                  </button>
                </div>
                {/* Priorität */}
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-xs text-gray-700 font-medium">Hohe Priorität</span>
                  </div>
                  <button
                    onClick={() => setPriority(p => p === 'normal' ? 'high' : 'normal')}
                    className={`w-9 h-5 rounded-full transition-colors relative ${
                      priority === 'high' ? 'bg-red-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white shadow-sm absolute top-0.5 transition-transform ${
                      priority === 'high' ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                {/* Lesebestätigung */}
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <CheckCheck className="w-3.5 h-3.5 text-gray-500" />
                    <span className="text-xs text-gray-700 font-medium">Lesebestätigung anfordern</span>
                  </div>
                  <button
                    onClick={() => setReadReceipt(r => !r)}
                    className={`w-9 h-5 rounded-full transition-colors relative ${
                      readReceipt ? 'bg-[#0E7490]' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white shadow-sm absolute top-0.5 transition-transform ${
                      readReceipt ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              </div>
            )}

            {/* Signatur-Hinweis — dynamisch je nach Konto */}
            {(() => {
              const selAcc = emailAccounts?.find((a: any) => a.id === selectedAccountId);
              const isMietGo = selAcc?.email === 'info@mietgo.de';
              const isLenneglas = selAcc?.email === 'info@lenneglas.de';
              const brandColor = isMietGo ? '#2D8A4E' : isLenneglas ? '#059669' : '#0E7490';
              const brandLabel = isMietGo ? 'MG' : isLenneglas ? 'LG' : 'GK';
              const brandName = isMietGo ? 'MietGo' : isLenneglas ? 'Lenneglas' : (senderName || 'Gutachter Koop');
              return (
                <div className="flex items-center gap-2 px-1 py-1 text-xs text-gray-400">
                  <div className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0" style={{ background: `${brandColor}15` }}>
                    <span className="text-[8px] font-bold" style={{ color: brandColor }}>{brandLabel}</span>
                  </div>
                  <span>Signatur von <strong className="text-gray-500">{brandName}</strong> wird angehängt</span>
                </div>
              );
            })()}
          </div>
        </div>

        {/* ===== FESTE TOOLBAR AM UNTEREN RAND ===== */}
        {/* Diese Toolbar ist IMMER sichtbar, auch auf Mobile über der Bottom-Navigation */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-3 py-2.5 mobile-dialog-actions sm:pb-2.5">
          <div className="flex items-center gap-2">
            {/* Anhänge Button */}
            <input ref={fileInputRef} type="file" multiple className="hidden"
              onChange={e => { if (e.target.files) handleAddAttachments(e.target.files); e.target.value = ''; }} />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
              title="Anhänge hinzufügen"
            >
              <Paperclip className="w-[18px] h-[18px]" />
            </button>

            {/* Später-Senden Toggle */}
            <button
              onClick={() => setShowSchedule(!showSchedule)}
              className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors ${
                showSchedule ? 'bg-amber-100 text-amber-700' : 'hover:bg-gray-100 text-gray-500'
              }`}
              title="Später senden"
            >
              <CalendarClock className="w-[18px] h-[18px]" />
            </button>

            {/* Optionen (Priorität, Lesebestätigung) */}
            <button
              onClick={() => setShowOptions(!showOptions)}
              className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors ${
                showOptions || priority === 'high' || readReceipt ? 'bg-gray-200 text-gray-700' : 'hover:bg-gray-100 text-gray-500'
              }`}
              title="Optionen"
            >
              <MoreHorizontal className="w-[18px] h-[18px]" />
            </button>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Abbrechen (nur Desktop) */}
            <button onClick={onClose}
              className="hidden sm:block text-sm font-medium text-gray-500 hover:text-gray-700 px-3 py-2 rounded-xl hover:bg-gray-100 transition-colors">
              Abbrechen
            </button>

            {/* Senden / Geplant senden Button */}
            {showSchedule && scheduleDate && scheduleTime ? (
              <button onClick={() => handleSend(true)} disabled={sending}
                className="flex items-center gap-2 bg-amber-600 text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-amber-700 transition-colors disabled:opacity-40 min-h-[44px] min-w-[120px] justify-center">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
                <span className="hidden sm:inline">Geplant senden</span>
                <span className="sm:hidden">Planen</span>
              </button>
            ) : (
              <button onClick={() => handleSend(false)} disabled={sending}
                className="flex items-center gap-2 bg-[#C41E3A] text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-[#A3172E] transition-colors disabled:opacity-40 min-h-[44px] min-w-[100px] justify-center">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Senden
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Gmail Connect Screen ─────────────────────────────────────────────────

function GmailConnectPrompt({ onConnect, loading }: { onConnect: () => void; loading: boolean }) {
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-blue-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Gmail verbinden</h3>
        <p className="text-sm text-gray-500 mb-6 leading-relaxed">
          Verbinde dein Gmail-Konto, um E-Mails direkt in der Unified Inbox zu sehen.
        </p>
        <button onClick={onConnect} disabled={loading}
          className="flex items-center gap-2 bg-[#C41E3A] text-white text-sm font-semibold px-6 py-3 rounded-xl hover:bg-[#A3172E] transition-colors mx-auto min-h-[44px]">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          Gmail-Konto verbinden
        </button>
      </div>
    </div>
  );
}

// ─── Leerer Zustand (Mitte) ────────────────────────────────────────────────

function EmptyCenter({ activeChannel }: { activeChannel: ChannelId }) {
  const cfg = CHANNEL_CONFIG[activeChannel] || CHANNEL_CONFIG.alle;
  const Icon = cfg.icon;

  return (
    <div className="flex-1 flex items-center justify-center bg-gray-50/50">
      <div className="text-center px-6">
        <div className="w-18 h-18 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: cfg.bgColor, width: '72px', height: '72px' }}>
          <Icon className="w-9 h-9" style={{ color: cfg.color }} />
        </div>
        <h3 className="text-xl font-bold text-gray-600 mb-2">
          {activeChannel === 'alle' ? 'Unified Inbox' : cfg.label}
        </h3>
        <p className="text-sm text-gray-500">
          Wähle eine Unterhaltung aus der Liste oder starte eine neue.
        </p>
      </div>
    </div>
  );
}

// ─── Bald verfügbar Platzhalter ────────────────────────────────────────────

function ComingSoonChannel({ channelId }: { channelId: ChannelId }) {
  const cfg = CHANNEL_CONFIG[channelId] || CHANNEL_CONFIG.alle;
  const Icon = cfg.icon;

  return (
    <div className="flex-1 flex items-center justify-center bg-gray-50/30">
      <div className="text-center px-6">
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 opacity-40" style={{ backgroundColor: cfg.bgColor }}>
          <Icon className="w-10 h-10" style={{ color: cfg.color }} />
        </div>
        <h3 className="text-lg font-bold text-gray-400 mb-2">{cfg.label}</h3>
        <p className="text-sm text-gray-400 mb-4">Dieser Kanal wird bald verfügbar sein.</p>
        <div className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-400 text-xs font-medium px-3 py-1.5 rounded-full">
          <Clock className="w-3 h-3" /> In Entwicklung
        </div>
      </div>
    </div>
  );
}

// ─── Neuer Chat Dialog ─────────────────────────────────────────────────────

function NewChatDialog({ onClose, onCreateChat }: {
  onClose: () => void;
  onCreateChat: (participantIds: string[], name?: string, type?: 'direct' | 'group') => Promise<string | null>;
}) {
  const { profile, impersonateUserId } = useAuth();
  const effectiveUserId = impersonateUserId || profile?.id;
  const [mitarbeiter, setMitarbeiter] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [groupName, setGroupName] = useState('');
  const [isGroup, setIsGroup] = useState(false);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('mitarbeiter')
        .select('id, vorname, nachname, rolle, avatar_url')
        .neq('id', effectiveUserId || '')
        .order('vorname');
      const enriched = (data || []).filter((m: any) => m.id !== KI_AGENT_ID && m.rolle !== 'ki_agent').map((m: any) => ({
        ...m,
        initialen: `${m.vorname?.[0] || ''}${m.nachname?.[0] || ''}`.toUpperCase(),
      }));
      setMitarbeiter(enriched);
      setLoading(false);
    }
    load();
  }, [effectiveUserId]);

  const filtered = mitarbeiter.filter(m => {
    const name = `${m.vorname} ${m.nachname}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const handleCreate = async () => {
    if (selected.length === 0) { toast.error('Wähle mindestens einen Teilnehmer'); return; }
    setCreating(true);
    const type = isGroup || selected.length > 1 ? 'group' : 'direct';
    const name = type === 'group' ? (groupName || `Gruppe (${selected.length + 1})`) : undefined;
    const chatId = await onCreateChat(selected, name, type);
    setCreating(false);
    if (chatId) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-label="Neuen Chat erstellen">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-200">
          <h3 className="text-base font-bold text-gray-900">Neuer Chat</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2.5">
            <Search className="w-4 h-4 text-gray-500" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Mitarbeiter suchen..."
              className="flex-1 bg-transparent text-sm outline-none text-gray-700 placeholder-gray-500" />
          </div>
        </div>
        <div className="px-4 py-2.5 flex items-center gap-3 border-b border-gray-100">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={isGroup} onChange={e => setIsGroup(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-[#C41E3A] focus:ring-[#C41E3A]" />
            <span className="text-sm text-gray-700">Gruppenchat</span>
          </label>
          {isGroup && (
            <input value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="Gruppenname..."
              className="flex-1 text-xs bg-gray-50 rounded-lg px-2.5 py-1.5 outline-none" />
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-300" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400">Keine Mitarbeiter gefunden</div>
          ) : (
            filtered.map(m => {
              const isSelected = selected.includes(m.id);
              return (
                <button key={m.id} onClick={() => setSelected(prev => isSelected ? prev.filter(id => id !== m.id) : [...prev, m.id])}
                  className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${isSelected ? 'bg-[#C41E3A]/5' : 'hover:bg-gray-50'}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${isSelected ? 'bg-[#C41E3A] text-white' : 'bg-gray-100 text-gray-600'}`}>
                    {isSelected ? <CheckCheck className="w-4 h-4" /> : m.initialen}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-800">{m.vorname} {m.nachname}</p>
                    <p className="text-xs text-gray-500 capitalize">{m.rolle}</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
        {selected.length > 0 && (
          <div className="px-4 py-3.5 border-t border-gray-200">
            <button onClick={handleCreate} disabled={creating}
              className="w-full flex items-center justify-center gap-2 bg-[#C41E3A] text-white text-sm font-bold py-3 rounded-xl hover:bg-[#A3172E] transition-colors disabled:opacity-50 min-h-[44px]">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Chat erstellen ({selected.length} Teilnehmer)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Aufgabe aus Nachricht erstellen Dialog ────────────────────────────────

function CreateAufgabeDialog({ message, onClose, onCreateAufgabe }: {
  message: ChatMessage;
  onClose: () => void;
  onCreateAufgabe: (messageId: string, titel: string) => Promise<void>;
}) {
  const [titel, setTitel] = useState(message.content?.slice(0, 100) || 'Aufgabe aus Chat');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    await onCreateAufgabe(message.id, titel);
    setCreating(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-label="Aufgabe aus Nachricht erstellen">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-800">Aufgabe erstellen</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="bg-gray-50 rounded-lg p-3 border-l-2 border-[#C41E3A]">
            <p className="text-xs text-gray-500 truncate">{message.content?.slice(0, 120)}</p>
          </div>
          <input value={titel} onChange={e => setTitel(e.target.value)} placeholder="Aufgabentitel..."
            className="w-full text-sm bg-gray-50 rounded-lg px-3 py-2.5 outline-none border border-gray-200 focus:border-[#C41E3A]" />
          <button onClick={handleCreate} disabled={creating}
            className="w-full flex items-center justify-center gap-2 bg-[#C41E3A] text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-[#A3172E] transition-colors disabled:opacity-50">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListTodo className="w-4 h-4" />}
            Aufgabe erstellen
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Chat-Bereich (Mitte, wenn Chat ausgewählt) ──────────────────────────

function ChatArea({
  chatRoom, messages, messagesLoading, sending, currentUserId,
  onSendMessage, onSendReply, onEditMessage, onUploadFile, onDeleteMessage, onCreateAufgabe, onBack,
}: {
  chatRoom: ChatRoom | null;
  messages: ChatMessage[];
  messagesLoading: boolean;
  sending: boolean;
  currentUserId: string;
  onSendMessage: (content: string) => Promise<void>;
  onSendReply: (content: string, replyToId: string) => Promise<void>;
  onEditMessage: (messageId: string, content: string) => Promise<void>;
  onUploadFile: (file: File) => Promise<void>;
  onDeleteMessage: (messageId: string) => Promise<void>;
  onCreateAufgabe: (messageId: string, titel: string) => Promise<void>;
  onBack: () => void;
}) {
  const [input, setInput] = useState('');
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);
  const [editInput, setEditInput] = useState('');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; message: ChatMessage } | null>(null);
  const [aufgabeMessage, setAufgabeMessage] = useState<ChatMessage | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    if (contextMenu) { document.addEventListener('click', handleClickOutside); return () => document.removeEventListener('click', handleClickOutside); }
  }, [contextMenu]);

  if (!chatRoom) return <EmptyCenter activeChannel="chat" />;

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    if (replyTo) { await onSendReply(text, replyTo.id); setReplyTo(null); }
    else { await onSendMessage(text); }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) { await onUploadFile(file); e.target.value = ''; }
  };

  const handleMoreClick = (e: React.MouseEvent, msg: ChatMessage) => {
    e.preventDefault(); e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, message: msg });
  };

  const handleStartEdit = (msg: ChatMessage) => {
    setEditingMessage(msg); setEditInput(msg.content || ''); setContextMenu(null);
  };
  const handleSaveEdit = async () => {
    if (editingMessage && editInput.trim()) { await onEditMessage(editingMessage.id, editInput.trim()); }
    setEditingMessage(null); setEditInput('');
  };
  const handleCancelEdit = () => { setEditingMessage(null); setEditInput(''); };

  const groupedMessages = useMemo(() => {
    const groups: { date: string; messages: ChatMessage[] }[] = [];
    for (const msg of messages) {
      const dateKey = new Date(msg.created_at).toDateString();
      const last = groups[groups.length - 1];
      if (last && last.date === dateKey) { last.messages.push(msg); }
      else { groups.push({ date: dateKey, messages: [msg] }); }
    }
    return groups;
  }, [messages]);

  const getChatName = () => {
    if (chatRoom.name) return chatRoom.name;
    if (chatRoom.type === 'direct') {
      const other = chatRoom.participants.find(p => p.mitarbeiter_id !== currentUserId);
      return other?.mitarbeiter ? `${other.mitarbeiter.vorname} ${other.mitarbeiter.nachname}` : 'Chat';
    }
    return `Gruppe (${chatRoom.participants.length})`;
  };

  return (
    <div className="flex-1 flex flex-col bg-white h-full min-w-0">
      {/* Chat Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-white min-h-[52px] flex-shrink-0">
        <button onClick={onBack} className="lg:hidden p-1.5 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-4 h-4 text-gray-500" />
        </button>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${
          chatRoom.type === 'group' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
        }`}>
          {chatRoom.type === 'group' ? <Users className="w-4 h-4" /> : 
            (() => { const other = chatRoom.participants.find(p => p.mitarbeiter_id !== currentUserId); return other?.mitarbeiter?.initialen || '?'; })()
          }
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-800 truncate">{getChatName()}</h3>
          <p className="text-[10px] text-gray-400">
            {chatRoom.type === 'group' ? `${chatRoom.participants.length} Teilnehmer` : 'Direktnachricht'}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => toast.info('Chat-Suche', { description: 'Durchsuche den Chat mit der Suchleiste links.' })}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
            aria-label="Im Chat suchen"
            title="Im Chat suchen"
          >
            <Search className="w-4 h-4 text-gray-400" />
          </button>
          <button
            onClick={() => toast.info('Chat-Einstellungen', { description: 'Weitere Chat-Optionen werden bald verfügbar sein.' })}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
            aria-label="Chat-Optionen"
            title="Chat-Optionen"
          >
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Nachrichten */}
      <div className="flex-1 overflow-y-auto px-4 py-3 bg-gray-50/30">
        {messagesLoading ? (
          <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400">Noch keine Nachrichten</p>
            </div>
          </div>
        ) : (
          <>
            {groupedMessages.map((group, gi) => (
              <div key={gi}>
                <div className="flex items-center justify-center my-3">
                  <span className="bg-white text-[10px] text-gray-400 px-3 py-1 rounded-full shadow-sm border border-gray-100">
                    {formatDateSeparator(group.date)}
                  </span>
                </div>
                {group.messages.map((msg, mi) => {
                  const isMine = msg.sender_id === currentUserId;
                  const showAvatar = !isMine && (mi === 0 || group.messages[mi - 1]?.sender_id !== msg.sender_id);
                  const showSenderName = mi === 0 || group.messages[mi - 1]?.sender_id !== msg.sender_id;
                  const isDeleted = msg.is_deleted;
                  const isEditing = editingMessage?.id === msg.id;
                  const FileIcon = getFileIcon(msg.file_type);

                  return (
                    <div key={msg.id} className={`group flex items-end gap-2 mb-1.5 ${isMine ? 'justify-end' : 'justify-start'}`}>
                      {!isMine && (
                        <div className="w-6 flex-shrink-0">
                          {showAvatar && (
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[9px] font-bold text-gray-600">
                              {msg.sender?.initialen || '?'}
                            </div>
                          )}
                        </div>
                      )}
                      {isMine && !isDeleted && (
                        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity order-0">
                          <button onClick={e => handleMoreClick(e, msg)} className="p-1 rounded-full hover:bg-gray-100 text-gray-300 hover:text-gray-600">
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      <div className={`relative max-w-[75%] sm:max-w-[65%] ${isMine ? 'order-1' : ''}`}>
                        {showSenderName && (
                          <p className={`text-[9px] font-semibold mb-0.5 ${isMine ? 'text-right mr-3 text-gray-400' : 'ml-3 text-gray-500'}`}>
                            {isMine ? 'Du' : `${msg.sender?.vorname || '?'} ${msg.sender?.nachname || ''}`}
                          </p>
                        )}
                        {isDeleted ? (
                          <div className={`rounded-2xl px-3 py-2 italic ${isMine ? 'bg-gray-200 text-gray-400 rounded-br-md' : 'bg-gray-100 text-gray-400 rounded-bl-md'}`}>
                            <div className="flex items-center gap-1.5"><Trash2 className="w-3 h-3" /><span className="text-xs">Gelöscht</span></div>
                          </div>
                        ) : isEditing ? (
                          <div className="bg-white rounded-2xl border-2 border-[#C41E3A]/30 px-3 py-2 shadow-lg">
                            <textarea value={editInput} onChange={e => setEditInput(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveEdit(); } if (e.key === 'Escape') handleCancelEdit(); }}
                              className="w-full text-sm text-gray-700 outline-none resize-none bg-transparent min-h-[32px]" autoFocus />
                            <div className="flex items-center justify-end gap-2 mt-1">
                              <button onClick={handleCancelEdit} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded">Abbrechen</button>
                              <button onClick={handleSaveEdit} className="text-xs text-white bg-[#C41E3A] hover:bg-[#A3172E] px-3 py-1 rounded-lg font-medium">Speichern</button>
                            </div>
                          </div>
                        ) : (
                          <div className={`rounded-2xl px-3 py-2 ${isMine ? 'bg-[#C41E3A] text-white rounded-br-md' : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100'}`}>
                            {msg.reply_message && (
                              <div className={`mb-1.5 pl-2 border-l-2 ${isMine ? 'border-white/40' : 'border-[#C41E3A]/40'}`}>
                                <p className={`text-[9px] font-semibold ${isMine ? 'text-white/70' : 'text-[#C41E3A]'}`}>{msg.reply_message.sender?.vorname || '?'}</p>
                                <p className={`text-[10px] truncate ${isMine ? 'text-white/60' : 'text-gray-400'}`}>{msg.reply_message.content?.slice(0, 60)}</p>
                              </div>
                            )}
                            {(msg.message_type === 'file' || msg.message_type === 'image') && msg.file_url ? (
                              msg.message_type === 'image' ? (
                                <img src={msg.file_url} alt={msg.file_name || 'Bild'} className="rounded-lg max-w-full max-h-[200px] object-cover mb-1" />
                              ) : (
                                <a href={msg.file_url} target="_blank" rel="noopener noreferrer"
                                  className={`flex items-center gap-2 p-2 rounded-lg mb-1 ${isMine ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-50 hover:bg-gray-100'}`}>
                                  <FileIcon className="w-4 h-4 flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-xs font-medium truncate">{msg.file_name || 'Datei'}</p>
                                    {msg.file_size && <p className={`text-[9px] ${isMine ? 'text-white/60' : 'text-gray-400'}`}>{formatFileSize(msg.file_size)}</p>}
                                  </div>
                                  <Download className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
                                </a>
                              )
                            ) : (
                              <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                            )}
                            {msg.edited_at && <span className={`text-[8px] ${isMine ? 'text-white/50' : 'text-gray-300'}`}> (bearbeitet)</span>}
                            <div className={`flex items-center justify-end gap-1 mt-0.5 ${isMine ? 'text-white/60' : 'text-gray-300'}`}>
                              <span className="text-[9px]">{formatMessageTime(msg.created_at)}</span>
                              {isMine && <CheckCheck className="w-3 h-3" />}
                            </div>
                          </div>
                        )}
                      </div>
                      {!isMine && !isDeleted && (
                        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={e => handleMoreClick(e, msg)} className="p-1 rounded-full hover:bg-gray-100 text-gray-300 hover:text-gray-600">
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Kontextmenü */}
      {contextMenu && (
        <div className="fixed z-50 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 min-w-[160px]"
          style={{ top: Math.min(contextMenu.y, window.innerHeight - 200), left: Math.min(contextMenu.x, window.innerWidth - 180) }}>
          <button onClick={() => { setReplyTo(contextMenu.message); setContextMenu(null); }}
            className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            <CornerUpLeft className="w-4 h-4 text-gray-400" /> Antworten
          </button>
          {contextMenu.message.sender_id === currentUserId && (
            <button onClick={() => handleStartEdit(contextMenu.message)}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
              <Pencil className="w-4 h-4 text-gray-400" /> Bearbeiten
            </button>
          )}
          <button onClick={() => { setAufgabeMessage(contextMenu.message); setContextMenu(null); }}
            className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            <ListTodo className="w-4 h-4 text-gray-400" /> Aufgabe erstellen
          </button>
          {contextMenu.message.sender_id === currentUserId && (
            <button onClick={() => { onDeleteMessage(contextMenu.message.id); setContextMenu(null); }}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
              <Trash2 className="w-4 h-4" /> Löschen
            </button>
          )}
        </div>
      )}

      {/* Reply-Banner */}
      {replyTo && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center gap-3 flex-shrink-0">
          <div className="flex-1 border-l-2 border-[#C41E3A] pl-3">
            <p className="text-[10px] font-semibold text-[#C41E3A]">{replyTo.sender?.vorname || '?'}</p>
            <p className="text-xs text-gray-500 truncate">{replyTo.content?.slice(0, 80)}</p>
          </div>
          <button onClick={() => setReplyTo(null)} className="p-1 hover:bg-gray-200 rounded"><X className="w-4 h-4 text-gray-400" /></button>
        </div>
      )}

      {/* Eingabefeld — über Bottom-Nav auf Mobile */}
      <div className="px-4 py-3 border-t border-gray-100 bg-white flex-shrink-0 mobile-dialog-actions sm:pb-3">
        <div className="flex items-end gap-2">
          <div className="flex-1 bg-gray-50 rounded-xl px-3 py-2 flex items-end gap-2 min-h-[44px]">
            <button onClick={() => fileInputRef.current?.click()} className="p-1 hover:bg-gray-200 rounded-lg flex-shrink-0 mb-0.5">
              <Paperclip className="w-4 h-4 text-gray-400" />
            </button>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Nachricht schreiben..."
              aria-label="Chat-Nachricht schreiben"
              className="flex-1 bg-transparent text-sm outline-none resize-none max-h-[120px] text-gray-700 placeholder-gray-400 py-0.5"
              rows={1}
            />
          </div>
          <button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C41E3A] text-white hover:bg-[#A3172E] transition-colors disabled:opacity-30 flex-shrink-0"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Aufgabe Dialog */}
      {aufgabeMessage && (
        <CreateAufgabeDialog message={aufgabeMessage} onClose={() => setAufgabeMessage(null)} onCreateAufgabe={onCreateAufgabe} />
      )}
    </div>
  );
}

// ─── Error Boundary ──────────────────────────────────────────────────────

class KommunikationErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Kommunikationsmodul Fehler:', error, info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-[calc(100vh-56px)] bg-white">
          <div className="text-center px-6 max-w-md">
            <AlertTriangle className="w-14 h-14 text-amber-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-gray-800 mb-2">Etwas ist schiefgelaufen</h2>
            <p className="text-sm text-gray-500 mb-4">
              Das Kommunikationsmodul konnte nicht geladen werden. Bitte versuche es erneut.
            </p>
            <p className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3 mb-4 text-left font-mono break-all">
              {this.state.error?.message || 'Unbekannter Fehler'}
            </p>
            <button
              onClick={() => { this.setState({ hasError: false, error: null }); }}
              className="inline-flex items-center gap-2 bg-[#C41E3A] text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-[#A3172E] transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Erneut versuchen
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Hauptkomponente: KommunikationPage ───────────────────────────────────

function KommunikationPageInner() {
  const { profile, impersonateUserId, teamMembers: authTeamMembers } = useAuth();
  const effectiveUserId = impersonateUserId || profile?.id || '';

  // Chat-Hook
  const {
    chatRooms, messages, loading: chatLoading, messagesLoading, sending,
    activeChatId, setActiveChatId, openChat,
    sendMessage, sendReply, editMessage, uploadFile, deleteMessage,
    createChat, createAufgabeFromMessage, markAsRead, loadChatRooms,
  } = useChat();

  // Gmail-Hook
  const gmail = useGmail();
  
  // Reconnect-Banner: Wenn Gmail-Token abgelaufen ist, Toast anzeigen
  useEffect(() => {
    if (gmail.needsReconnect) {
      toast.error('Gmail-Zugriff abgelaufen! Bitte unter Einstellungen > Integrationen erneut verbinden.', {
        duration: 15000,
        id: 'gmail-reconnect-warning',
      });
    }
  }, [gmail.needsReconnect]);

  // Multi-Account E-Mail Hooks
  const emailAccountsRaw = useEmailAccounts();
  
  // Gmail als virtuelles Konto hinzufügen, falls nicht bereits in email_accounts
  const emailAccounts = useMemo(() => {
    const hasGmailInAccounts = emailAccountsRaw.accounts.some(a => a.provider === 'gmail');
    if (gmail.isConnected && gmail.connectedEmail && !hasGmailInAccounts) {
      const virtualGmailAccount: EmailAccount = {
        id: '__gmail__',
        name: 'Gutachter Koop (Gmail)',
        email: gmail.connectedEmail,
        provider: 'gmail',
        is_active: true,
        is_default: emailAccountsRaw.accounts.length === 0,
        color: '#2563EB',
        last_sync_at: null,
        last_sync_error: null,
        auto_sync: false,
        signature_html: null,
        signature_text: null,
      };
      return {
        ...emailAccountsRaw,
        accounts: [virtualGmailAccount, ...emailAccountsRaw.accounts],
      };
    }
    return emailAccountsRaw;
  }, [emailAccountsRaw, gmail.isConnected, gmail.connectedEmail]);
  const ionosAccountId = emailAccounts.accounts.find(a => a.provider === 'ionos' || a.provider === 'imap_smtp')?.id || null;
  const ionosMessages = useEmailMessages(ionosAccountId);
  // Für Filter/Signaturen: Bei "Alle Konten" (__all__) das Default-Konto verwenden
  const effectiveAccountId = emailAccounts.selectedAccountId === '__all__' 
    ? (emailAccounts.accounts.find(a => a.is_default)?.id || emailAccounts.accounts[0]?.id || null)
    : emailAccounts.selectedAccountId;
  const emailFiltersHook = useEmailFilters(effectiveAccountId);
  const emailSignaturesHook = useEmailSignatures(effectiveAccountId);

  // WhatsApp & SMS Hook
  const messaging = useMessaging();

  // Telefon-Hook
  const phone = usePhoneSystem();

  // KI-Dokumentenanalyse
  const docAI = useDocAI();

  // Konversations-Status (Offen/Später/Erledigt) + Notizen + Zuordnungen
  const convStatus = useConversationStatus();
  // Team members from auth context for assignments
  const contactPanelTeamMembers = useMemo(() =>
    (authTeamMembers || []).map(m => ({ id: m.id, vorname: m.vorname, nachname: m.nachname, rolle: m.rolle })),
    [authTeamMembers]
  );

  // State
  const [activeChannel, setActiveChannel] = useState<ChannelId>('alle');
  const [statusFilter, setStatusFilter] = useState<ConversationStatus>('offen');
  const [searchQuery, setSearchQuery] = useState('');
  const [emailFolder, setEmailFolder] = useState('INBOX');
  const [showNewChat, setShowNewChat] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [replyToEmail, setReplyToEmail] = useState<GmailMessage | null>(null);
  const [forwardEmail, setForwardEmail] = useState<GmailMessage | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<UnifiedConversation | null>(null);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const [emailForTask, setEmailForTask] = useState<GmailMessage | null>(null);
  const [showEmailFilters, setShowEmailFilters] = useState(false);
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  const [showNewWhatsApp, setShowNewWhatsApp] = useState(false);
  const [showNewSMS, setShowNewSMS] = useState(false);
  const [showPhoneDialer, setShowPhoneDialer] = useState(false);
  const [selectedPhoneCall, setSelectedPhoneCall] = useState<PhoneCallType | null>(null);
  const [phoneCallFilter, setPhoneCallFilter] = useState<'alle' | 'eingehend' | 'ausgehend' | 'verpasst'>('alle');

  // Gmail laden beim Mount UND bei Kanal-/Ordner-Wechsel
  // ROBUST: Prüfe isConnected UND dass nicht bereits geladen wird
  const gmailInitialLoadDone = useRef(false);
  useEffect(() => {
    if (gmail.isConnected && !gmail.loading) {
      // Beim ersten Mal oder bei Ordner-Wechsel laden
      if (!gmailInitialLoadDone.current || emailFolder) {
        gmailInitialLoadDone.current = true;
        gmail.fetchMessages(emailFolder);
      }
    }
  }, [gmail.isConnected, gmail.loading, emailFolder]);

  // Thread-State: Wenn E-Mail ausgewählt, Thread laden
  const [threadMessages, setThreadMessages] = useState<GmailMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);

  // Mobile Back-Button: Detail-Ansicht schließen statt Seite wechseln
  useMobileDetailBackHandler(mobileShowDetail, () => {
    setSelectedConversation(null);
    setMobileShowDetail(false);
    setActiveChatId(null);
    setThreadMessages([]);
  });

  // Globaler Event-Listener: Anruf von KontaktDetail oder anderen Komponenten
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.number) {
        phone.makeCall(detail.number, detail.name);
      }
    };
    window.addEventListener('koop:make-call', handler);
    return () => window.removeEventListener('koop:make-call', handler);
  }, [phone.makeCall]);

  const loadThread = useCallback(async (threadId: string, fromEmail?: string) => {
    setThreadLoading(true);
    try {
      const msgs = await gmail.getThread(threadId, fromEmail);
      // Chronologisch sortieren (älteste zuerst)
      msgs.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      setThreadMessages(msgs);
    } catch (err: any) {
      console.error('Thread laden fehlgeschlagen:', err);
      // Retry einmal bei Netzwerkfehler
      if (err?.message?.includes('fetch') || err?.message?.includes('network') || err?.message?.includes('Failed')) {
        try {
          await new Promise(r => setTimeout(r, 1000));
          const msgs = await gmail.getThread(threadId, fromEmail);
          msgs.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
          setThreadMessages(msgs);
          return;
        } catch { /* Retry fehlgeschlagen */ }
      }
      setThreadMessages([]);
      toast.error('E-Mail konnte nicht geladen werden. Bitte erneut versuchen.');
    } finally {
      setThreadLoading(false);
    }
  }, [gmail]);

  // ─── Unified Conversations bauen ──────────────────────────────────────

  const unifiedConversations = useMemo(() => {
    const convs: UnifiedConversation[] = [];

    // 1. Chat-Unterhaltungen hinzufügen
    if (activeChannel === 'alle' || activeChannel === 'chat') {
      for (const room of (chatRooms || [])) {
        const other = room.type === 'direct'
          ? room.participants.find(p => p.mitarbeiter_id !== effectiveUserId)
          : null;
        const name = room.name || (other?.mitarbeiter ? `${other.mitarbeiter.vorname} ${other.mitarbeiter.nachname}` : `Gruppe (${room.participants.length})`);
        const lastMsg = room.last_message;
        const initials = other?.mitarbeiter
          ? getInitials(other.mitarbeiter.vorname, other.mitarbeiter.nachname)
          : room.type === 'group' ? 'GR' : '??';

        convs.push({
          id: `chat-${room.id}`,
          channel: 'chat',
          name,
          preview: lastMsg?.content || 'Keine Nachrichten',
          timestamp: lastMsg?.created_at || room.created_at,
          unreadCount: room.unread_count || 0,
          status: convStatus.getStatus(`chat-${room.id}`) as ConversationStatus,
          avatarInitials: initials,
          chatRoom: room,
          contactEmail: other?.mitarbeiter ? '' : undefined,
        });
      }
    }

    // 2. E-Mails als Unterhaltungen hinzufügen — nach threadId gruppiert
    // Alle Gmail-Konten für korrekte Zuordnung
    const gmailAccounts = emailAccounts.accounts.filter(a => a.provider === 'gmail');
    const gmailAccount = gmailAccounts[0]; // Fallback
    const ionosAccount = emailAccounts.accounts.find(a => a.provider === 'ionos' || a.provider === 'imap_smtp');
    // Helper: Finde das richtige Gmail-Konto für eine E-Mail anhand from/to
    const findOwnerAccount = (email: GmailMessage) => {
      if (gmailAccounts.length <= 1) return gmailAccount;
      const from = (email.from || '').toLowerCase();
      const to = (email.to || '').toLowerCase();
      for (const acc of gmailAccounts) {
        const accEmail = acc.email.toLowerCase();
        if (from.includes(accEmail) || to.includes(accEmail)) return acc;
      }
      return gmailAccount; // Fallback zum ersten Gmail-Konto
    };

    if ((activeChannel === 'alle' || activeChannel === 'email') && gmail.isConnected) {
      const threadMap = new Map<string, GmailMessage[]>();
      for (const email of gmail.messages) {
        const tid = email.threadId || email.id;
        if (!threadMap.has(tid)) threadMap.set(tid, []);
        threadMap.get(tid)!.push(email);
      }

      for (const [threadId, threadEmails] of Array.from(threadMap.entries())) {
        // Neueste E-Mail im Thread für Preview
        const newest = threadEmails.reduce((a: GmailMessage, b: GmailMessage) => new Date(a.date) > new Date(b.date) ? a : b);
        // Älteste für den ursprünglichen Absender
        const oldest = threadEmails.reduce((a: GmailMessage, b: GmailMessage) => new Date(a.date) < new Date(b.date) ? a : b);
        const senderName = extractName(oldest.from);
        const senderEmail = extractEmail(oldest.from);
        const initials = getEmailInitials(senderName);
        const unreadInThread = threadEmails.filter((e: GmailMessage) => e.labelIds?.includes('UNREAD')).length;
        const threadCount = threadEmails.length;

        convs.push({
          id: `email-${threadId}`,
          channel: 'email',
          name: senderName + (threadCount > 1 ? ` (${threadCount})` : ''),
          subtitle: oldest.subject || '(Kein Betreff)',
          preview: newest.snippet || '',
          timestamp: newest.date,
          unreadCount: unreadInThread,
          status: convStatus.getStatus(`email-${threadId}`) as ConversationStatus,
          avatarInitials: initials,
          emailMessage: newest, // Neueste für Quick-View
          emailThreadId: threadId,
          contactEmail: senderEmail,
          emailAccountId: findOwnerAccount(oldest)?.id || gmailAccount?.id,
          emailAccountColor: getCompanyColor(findOwnerAccount(oldest)?.email || gmailAccount?.email || '', findOwnerAccount(oldest)?.name || gmailAccount?.name),
        });
      }
    }

    // 3. IONOS/IMAP-E-Mails als Unterhaltungen hinzufügen
    if ((activeChannel === 'alle' || activeChannel === 'email') && ionosMessages.emails.length > 0) {
      const ionosThreadMap = new Map<string, typeof ionosMessages.emails>();
      for (const email of ionosMessages.emails) {
        const tid = email.thread_id || email.id;
        if (!ionosThreadMap.has(tid)) ionosThreadMap.set(tid, []);
        ionosThreadMap.get(tid)!.push(email);
      }
      for (const [threadId, threadEmails] of Array.from(ionosThreadMap.entries())) {
        const newest = threadEmails.reduce((a, b) => new Date(a.date) > new Date(b.date) ? a : b);
        const oldest = threadEmails.reduce((a, b) => new Date(a.date) < new Date(b.date) ? a : b);
        const senderName = oldest.from_name || oldest.from_email.split('@')[0] || 'Unbekannt';
        const initials = getEmailInitials(senderName);
        const unreadInThread = threadEmails.filter(e => !e.is_read).length;
        const threadCount = threadEmails.length;
        // IONOS EmailMessage → GmailMessage-kompatibles Format konvertieren
        const toGmailFormat = (em: typeof newest): GmailMessage => ({
          id: em.id,
          threadId: em.thread_id || em.id,
          snippet: em.snippet || '',
          labelIds: em.is_read ? [] : ['UNREAD'],
          from: em.from_name ? `${em.from_name} <${em.from_email}>` : em.from_email,
          to: (em.to_emails || []).join(', '),
          cc: (em.cc_emails || []).join(', '),
          subject: em.subject || '',
          date: em.date,
          isUnread: !em.is_read,
          body: em.body_text || '',
          bodyHtml: em.body_html || '',
          attachments: (em.attachments || []).map((a: any) => ({
            filename: a.filename || a.name || 'Anhang',
            mimeType: a.contentType || a.mimeType || 'application/octet-stream',
            attachmentId: a.id || a.filename || '',
            size: a.size || 0,
          })),
        });

        convs.push({
          id: `ionos-${threadId}`,
          channel: 'email',
          name: senderName + (threadCount > 1 ? ` (${threadCount})` : ''),
          subtitle: oldest.subject || '(Kein Betreff)',
          preview: newest.snippet || '',
          timestamp: newest.date,
          unreadCount: unreadInThread,
          status: convStatus.getStatus(`ionos-${threadId}`) as ConversationStatus,
          avatarInitials: initials,
          contactEmail: oldest.from_email,
          emailAccountId: ionosAccount?.id,
          emailAccountColor: getCompanyColor(ionosAccount?.email || '', ionosAccount?.name),
          emailMessage: toGmailFormat(newest),
          emailThreadId: threadId,
        });
      }
    }

    // 4. WhatsApp-Unterhaltungen hinzufügen
    if (activeChannel === 'alle' || activeChannel === 'whatsapp') {
      for (const conv of messaging.whatsappConversations) {
        const displayName = conv.contactName || conv.phoneNumber;
        const initials = conv.contactName
          ? conv.contactName.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()
          : conv.phoneNumber.slice(-2);

        convs.push({
          id: `wa-${conv.conversationId}`,
          channel: 'whatsapp',
          name: displayName,
          preview: conv.lastMessage.body || (conv.lastMessage.media_urls ? '📷 Bild' : ''),
          timestamp: conv.lastMessage.created_at,
          unreadCount: conv.unreadCount,
          status: convStatus.getStatus(`wa-${conv.conversationId}`) as ConversationStatus,
          avatarInitials: initials,
          contactPhone: conv.phoneNumber,
          originalId: conv.conversationId,
        });
      }
    }

    // 5. SMS-Unterhaltungen hinzufügen
    if (activeChannel === 'alle' || activeChannel === 'sms') {
      for (const conv of messaging.smsConversations) {
        const displayName = conv.contactName || conv.phoneNumber;
        const initials = conv.contactName
          ? conv.contactName.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()
          : conv.phoneNumber.slice(-2);

        convs.push({
          id: `sms-${conv.conversationId}`,
          channel: 'sms' as ChannelId,
          name: displayName,
          preview: conv.lastMessage.body || '',
          timestamp: conv.lastMessage.created_at,
          unreadCount: conv.unreadCount,
          status: convStatus.getStatus(`sms-${conv.conversationId}`) as ConversationStatus,
          avatarInitials: initials,
          contactPhone: conv.phoneNumber,
          originalId: conv.conversationId,
        });
      }
    }

    // 6. Telefon-Anrufe hinzufügen
    if (activeChannel === 'alle' || activeChannel === 'telefon') {
      for (const call of phone.calls) {
        const displayName = call.contact_name || (call.direction === 'inbound' ? call.from_number : call.to_number);
        const initials = call.contact_name
          ? call.contact_name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()
          : '📞';

        const statusLabel = call.status === 'missed' || call.status === 'no-answer' ? '❌ Verpasst' :
          call.status === 'voicemail' ? '📩 Sprachnachricht' :
          call.status === 'completed' ? (call.direction === 'inbound' ? '📥 Eingehend' : '📤 Ausgehend') :
          call.status;

        const durationStr = call.duration_seconds > 0
          ? ` · ${Math.floor(call.duration_seconds / 60)}:${String(call.duration_seconds % 60).padStart(2, '0')}`
          : '';

        convs.push({
          id: `phone-${call.id}`,
          channel: 'telefon',
          name: displayName,
          preview: `${statusLabel}${durationStr}`,
          timestamp: call.started_at,
          unreadCount: (call.status === 'missed' || call.status === 'no-answer' || call.status === 'voicemail') && !call.callback_completed_at ? 1 : 0,
          status: convStatus.getStatus(`phone-${call.id}`) as ConversationStatus,
          avatarInitials: initials,
          contactPhone: call.direction === 'inbound' ? call.from_number : call.to_number,
        });
      }
    }

    // Chronologisch sortieren (neueste zuerst)
    convs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return convs;
  }, [chatRooms, gmail.messages, gmail.isConnected, activeChannel, effectiveUserId, emailAccounts.accounts, convStatus.statusMap, ionosMessages.emails, messaging.whatsappConversations, messaging.smsConversations, phone.calls]);

  // Kanal-Counts berechnen (Gmail + IONOS)
  const channelCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    let totalUnread = 0;

    // Chat unread
    const chatUnread = (chatRooms || []).reduce((sum, r) => sum + (r.unread_count || 0), 0);
    counts.chat = chatUnread;
    totalUnread += chatUnread;

    // Email unread (Gmail + IONOS)
    let emailUnread = 0;
    if (gmail.isConnected) {
      emailUnread += gmail.messages.filter(m => m.labelIds?.includes('UNREAD')).length;
    }
    if (ionosMessages.emails.length > 0) {
      emailUnread += ionosMessages.emails.filter(e => !e.is_read).length;
    }
    counts.email = emailUnread;
    totalUnread += emailUnread;

    // WhatsApp unread
    const waUnread = messaging.whatsappUnreadCount;
    counts.whatsapp = waUnread;
    totalUnread += waUnread;

    // SMS unread
    const smsUnread = messaging.smsUnreadCount;
    counts.sms = smsUnread;
    totalUnread += smsUnread;

    // Telefon unread (verpasste Anrufe + Rückrufbitten)
    const phoneUnread = phone.unreadCount;
    counts.telefon = phoneUnread;
    totalUnread += phoneUnread;

    counts.alle = totalUnread;
    return counts;
  }, [chatRooms, gmail.messages, gmail.isConnected, ionosMessages.emails, messaging.whatsappUnreadCount, messaging.smsUnreadCount, phone.unreadCount]);

  // ─── Aktionen ─────────────────────────────────────────────────────────

  const handleSelectConversation = useCallback((conv: UnifiedConversation) => {
    setSelectedConversation(conv);
    setMobileShowDetail(true);
    if (conv.channel === 'chat' && conv.chatRoom) {
      openChat(conv.chatRoom.id);
    }
    if (conv.channel === 'email' && conv.emailThreadId) {
      // IONOS-E-Mails: Thread aus ionosMessages laden (nicht über Gmail-API)
      if (conv.id.startsWith('ionos-')) {
        // Thread-Nachrichten aus den bereits geladenen IONOS-Mails zusammenstellen
        const ionosThreadEmails = ionosMessages.emails
          .filter(e => (e.thread_id || e.id) === conv.emailThreadId)
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
          .map(em => ({
            id: em.id,
            threadId: em.thread_id || em.id,
            snippet: em.snippet || '',
            labelIds: em.is_read ? [] : ['UNREAD'],
            from: em.from_name ? `${em.from_name} <${em.from_email}>` : em.from_email,
            to: (em.to_emails || []).join(', '),
            cc: (em.cc_emails || []).join(', '),
            subject: em.subject || '',
            date: em.date,
            isUnread: !em.is_read,
            body: em.body_text || '',
            bodyHtml: em.body_html || '',
            attachments: (em.attachments || []).map((a: any) => ({
              filename: a.filename || a.name || 'Anhang',
              mimeType: a.contentType || a.mimeType || 'application/octet-stream',
              attachmentId: a.id || a.filename || '',
              size: a.size || 0,
            })),
          } as GmailMessage));
        setThreadMessages(ionosThreadEmails);
        // ALLE ungelesenen E-Mails im Thread als gelesen markieren
        const unreadIonosInThread = ionosMessages.emails
          .filter(e => (e.thread_id || e.id) === conv.emailThreadId && !e.is_read);
        for (const unreadEmail of unreadIonosInThread) {
          ionosMessages.markRead(unreadEmail.id, true);
        }
      } else {
        // Gmail-E-Mails: Thread über Gmail-API laden
        // Multi-Account: Bestimme welches Gmail-Konto diesen Thread besitzt
        const convAccount = emailAccounts.accounts.find((a: any) => a.id === conv.emailAccountId);
        const accountEmail = convAccount?.email || undefined;
        loadThread(conv.emailThreadId, accountEmail);
        // ALLE ungelesenen E-Mails im Gmail-Thread als gelesen markieren
        const unreadGmailInThread = (gmail.messages || []).filter(
          (m: GmailMessage) => m.threadId === conv.emailThreadId && m.labelIds?.includes('UNREAD')
        );
        for (const unreadMsg of unreadGmailInThread) {
          gmail.markAsRead(unreadMsg.id);
        }
      }
    }
    // WhatsApp/SMS: Conversation öffnen und als gelesen markieren
    if ((conv.channel === 'whatsapp' || conv.channel === 'sms') && conv.originalId) {
      messaging.openConversation(conv.originalId);
    }
    // Telefon: Anruf-Detail laden
    if (conv.channel === 'telefon') {
      const callId = conv.id.replace('phone-', '');
      const call = phone.calls.find(c => c.id === callId);
      if (call) setSelectedPhoneCall(call);
    }
  }, [openChat, loadThread, gmail, ionosMessages, messaging, phone.calls, emailAccounts.accounts]);

  const handleBack = useCallback(() => {
    setSelectedConversation(null);
    setMobileShowDetail(false);
    setActiveChatId(null);
    setThreadMessages([]);
    messaging.setActiveConversationId(null);
    setSelectedPhoneCall(null);
  }, [setActiveChatId]);

  // Auto-Advance: Nach Statusänderung / Löschen / Archivieren zur nächsten ungelesenen Nachricht springen
  const advanceToNext = useCallback(() => {
    if (!selectedConversation) { handleBack(); return; }
    // Finde die nächste Konversation in der aktuellen Liste (gleicher Status-Filter)
    const currentList = unifiedConversations.filter(c => c.status === statusFilter);
    const currentIdx = currentList.findIndex(c => c.id === selectedConversation.id);
    // Präferenz: Nächste ungelesene, sonst nächste in der Liste
    const remaining = currentList.filter((c, i) => i !== currentIdx);
    const nextUnread = remaining.find(c => c.unreadCount > 0);
    const nextInList = currentIdx < currentList.length - 1 ? currentList[currentIdx + 1] : currentList[0];
    const next = nextUnread || (remaining.length > 0 ? (nextInList && nextInList.id !== selectedConversation.id ? nextInList : remaining[0]) : null);
    if (next) {
      handleSelectConversation(next);
      toast.success('Nächste Nachricht');
    } else {
      handleBack();
      toast.success('Alle Nachrichten abgearbeitet');
    }
  }, [selectedConversation, unifiedConversations, statusFilter, handleBack]);

  // Status setzen + Auto-Advance
  const currentUserName = profile ? `${profile.vorname || ''} ${profile.nachname || ''}`.trim() || 'Unbekannt' : 'Unbekannt';
  const handleSetStatusAndAdvance = useCallback((status: 'offen' | 'spaeter' | 'erledigt') => {
    if (!selectedConversation) return;
    convStatus.setStatus(selectedConversation.id, status as any, effectiveUserId, currentUserName);
    const statusLabels = { offen: 'Offen', spaeter: 'Später', erledigt: 'Erledigt' };
    toast.success(`Verschoben nach: ${statusLabels[status]}`);
    // Wenn der neue Status nicht dem aktuellen Filter entspricht, advance
    if (status !== statusFilter) {
      advanceToNext();
    }
  }, [selectedConversation, convStatus, statusFilter, advanceToNext, effectiveUserId, currentUserName]);

  const handleSendChatMessage = useCallback(async (content: string) => {
    if (!activeChatId) return;
    await sendMessage(activeChatId, content);
  }, [sendMessage, activeChatId]);

  const handleSendChatReply = useCallback(async (content: string, replyToId: string) => {
    if (!activeChatId) return;
    await sendReply(activeChatId, content, replyToId);
  }, [sendReply, activeChatId]);

  const handleEditMessage = useCallback(async (messageId: string, content: string) => {
    await editMessage(messageId, content);
  }, [editMessage]);

  const handleUploadFile = useCallback(async (file: File) => {
    if (!activeChatId) return;
    const fileData = await uploadFile(file);
    if (fileData) {
      await sendMessage(activeChatId, file.name, file.type.startsWith('image/') ? 'image' : 'file', fileData);
    }
  }, [uploadFile, sendMessage, activeChatId]);

  const handleDeleteMessage = useCallback(async (messageId: string) => {
    await deleteMessage(messageId);
  }, [deleteMessage]);

  const handleCreateAufgabe = useCallback(async (messageId: string, titel: string) => {
    // Finde die Nachricht in den aktuellen Messages
    const msg = messages.find(m => m.id === messageId);
    if (msg) {
      await createAufgabeFromMessage(msg, titel);
    }
  }, [createAufgabeFromMessage, messages]);

  const handleCreateChat = useCallback(async (participantIds: string[], name?: string, type?: 'direct' | 'group') => {
    const chatId = await createChat(participantIds, name, type);
    if (chatId) {
      setActiveChannel('chat');
      openChat(chatId);
      const room = (chatRooms || []).find(r => r.id === chatId);
      if (room) {
        handleSelectConversation({
          id: `chat-${chatId}`,
          channel: 'chat',
          name: name || 'Neuer Chat',
          timestamp: new Date().toISOString(),
          unreadCount: 0,
          status: 'offen',
          chatRoom: room,
        });
      }
    }
    return chatId;
  }, [createChat, chatRooms, openChat, handleSelectConversation]);

  const handleRefreshEmails = useCallback(() => {
    if (gmail.isConnected) {
      gmail.refresh();
    }
  }, [gmail]);

  // Auto-select info@mietgo.de when composing new mail from a MietGo conversation context
  const handleNewEmail = useCallback(() => {
    // Auto-Select: Wenn "Alle" aktiv ist, das Konto der aktuellen Konversation vorauswählen
    if (emailAccounts.selectedAccountId === '__all__' && selectedConversation?.emailAccountId) {
      const convAccount = emailAccounts.accounts.find(
        (a: any) => a.id === selectedConversation.emailAccountId
      );
      if (convAccount) {
        emailAccounts.setSelectedAccountId(convAccount.id);
      }
    }
    setShowCompose(true);
  }, [selectedConversation, emailAccounts]);

  const handleSendEmail = useCallback(async (to: string, subject: string, body: string, cc?: string, bcc?: string, inReplyTo?: string, references?: string, attachments?: EmailAttachment[]) => {
    // Determine which account to use:
    // 1. If a specific account is selected (not __all__), use it
    // 2. If __all__ is selected, detect from the email body signature or use default
    let selectedAcc = emailAccounts.accounts.find((a: any) => a.id === emailAccounts.selectedAccountId);
    
    // If __all__ mode or no account found, try to detect from signature in body
    if (!selectedAcc || emailAccounts.selectedAccountId === '__all__') {
      // Check if body contains IONOS account markers
      const ionosAcc = emailAccounts.accounts.find((a: any) => 
        a.provider !== 'gmail' && body.includes(a.email)
      );
      const gmailAcc = emailAccounts.accounts.find((a: any) => 
        a.provider === 'gmail' && body.includes(a.email)
      );
      // Use the account whose email appears in the body (signature), otherwise default
      selectedAcc = ionosAcc || gmailAcc || emailAccounts.accounts.find((a: any) => a.is_default) || emailAccounts.accounts[0];
    }
    
    // Route based on selected account provider
    if (selectedAcc && selectedAcc.provider !== 'gmail') {
      // IONOS/IMAP — send via email-api
      const resp = await fetch('/api/email-api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'sendEmail',
          accountId: selectedAcc.id,
          to,
          cc: cc || undefined,
          bcc: bcc || undefined,
          subject,
          bodyHtml: body,
          bodyText: body.replace(/<[^>]*>/g, ''),
          inReplyTo,
          attachments: attachments?.map(a => ({
            filename: a.filename,
            content: a.base64Data,
            contentType: a.mimeType,
          })),
        }),
      });
      const result = await resp.json();
      if (!resp.ok) throw new Error(result.error || 'Fehler beim Senden über IONOS');
      return result;
    } else {
      // Gmail — send via gmail-api (multi-account: pass fromEmail for correct token lookup)
      const fromEmail = selectedAcc?.email || undefined;
      return gmail.sendEmail(to, subject, body, cc, bcc, inReplyTo, references, attachments, fromEmail);
    }
  }, [gmail, emailAccounts.accounts, emailAccounts.selectedAccountId]);

  // ─── Tastaturkürzel (Keyboard Shortcuts) ─────────────────────────────
  // R = Antworten, A = Archivieren, E = Erledigt, # = Papierkorb, Escape = Zurück
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Nicht auslösen wenn im Textfeld
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) return;
      // Nicht auslösen bei Modifier-Tasten
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (!selectedConversation) return;
      const isEmail = selectedConversation.channel === 'email' && selectedConversation.emailMessage;

      switch (e.key.toLowerCase()) {
        case 'r':
          if (isEmail) {
            e.preventDefault();
            setReplyToEmail(selectedConversation.emailMessage!);
            setShowCompose(true);
          }
          break;
        case 'a':
          if (isEmail) {
            e.preventDefault();
            const isIonos = selectedConversation.id.startsWith('ionos-');
            if (isIonos) {
              ionosMessages.moveEmail(selectedConversation.emailMessage!.id, 'Archive');
            } else {
              gmail.archiveMessage(selectedConversation.emailMessage!.id);
            }
            handleSetStatusAndAdvance('erledigt');
          }
          break;
        case 'e':
          e.preventDefault();
          handleSetStatusAndAdvance('erledigt');
          break;
        case '#':
          if (isEmail) {
            e.preventDefault();
            const isIonos = selectedConversation.id.startsWith('ionos-');
            if (isIonos) {
              ionosMessages.deleteEmail(selectedConversation.emailMessage!.id);
            } else {
              gmail.trashMessage(selectedConversation.emailMessage!.id);
            }
            advanceToNext();
          }
          break;
        case 'escape':
          e.preventDefault();
          handleBack();
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedConversation, gmail, ionosMessages, handleSetStatusAndAdvance, advanceToNext, handleBack]);

  // ─── Render ───────────────────────────────────────────────────────────

  const selectedRoom = selectedConversation?.chatRoom || null;

  // Bestimme was in der Mitte angezeigt wird
  const renderCenter = () => {
    if (!selectedConversation) {
      return <EmptyCenter activeChannel={activeChannel} />;
    }

    if (selectedConversation.channel === 'chat') {
      return (
        <ChatArea
          chatRoom={selectedRoom}
          messages={messages}
          messagesLoading={messagesLoading}
          sending={sending}
          currentUserId={effectiveUserId}
          onSendMessage={handleSendChatMessage}
          onSendReply={handleSendChatReply}
          onEditMessage={handleEditMessage}
          onUploadFile={handleUploadFile}
          onDeleteMessage={handleDeleteMessage}
          onCreateAufgabe={handleCreateAufgabe}
          onBack={handleBack}
        />
      );
    }

    if (selectedConversation.channel === 'email' && selectedConversation.emailMessage) {
      // Thread-Ansicht: Alle Nachrichten des Threads anzeigen
      const displayMessages = threadMessages.length > 0 ? threadMessages : [selectedConversation.emailMessage];
      const isIonos = selectedConversation.id.startsWith('ionos-');
      return (
        <EmailThreadView
          messages={displayMessages}
          threadLoading={threadLoading}
          subject={selectedConversation.subtitle || selectedConversation.emailMessage.subject || '(Kein Betreff)'}
          onBack={handleBack}
          onReply={(msg) => {
            // Auto-Select: Konto der Konversation vorauswählen bei Antwort
            if (selectedConversation.emailAccountId) {
              const convAcc = emailAccounts.accounts.find((a: any) => a.id === selectedConversation.emailAccountId);
              if (convAcc) emailAccounts.setSelectedAccountId(convAcc.id);
            }
            setReplyToEmail(msg); setShowCompose(true);
          }}
          onForward={(msg) => {
            if (selectedConversation.emailAccountId) {
              const convAcc = emailAccounts.accounts.find((a: any) => a.id === selectedConversation.emailAccountId);
              if (convAcc) emailAccounts.setSelectedAccountId(convAcc.id);
            }
            setForwardEmail(msg); setShowCompose(true);
          }}
          onMarkRead={(msg) => isIonos ? ionosMessages.markRead(msg.id, true) : gmail.markAsRead(msg.id)}
          onMarkUnread={(msg) => isIonos ? ionosMessages.markRead(msg.id, false) : gmail.markAsUnread(msg.id)}
          onTrash={(msg) => {
            if (isIonos) {
              ionosMessages.deleteEmail(msg.id);
            } else {
              gmail.trashMessage(msg.id);
            }
            advanceToNext();
          }}
          onArchive={(msg) => {
            if (isIonos) {
              ionosMessages.moveEmail(msg.id, 'Archive');
            } else {
              gmail.archiveMessage(msg.id);
            }
            handleSetStatusAndAdvance('erledigt');
          }}
          onSpam={isIonos ? undefined : (msg) => {
            gmail.reportSpam(msg.id);
            toast.success('Als Spam markiert');
            advanceToNext();
          }}
          onDownloadAttachment={isIonos ? undefined : (messageId: string, attachmentId: string, filename: string, mimeType: string) => {
            const convAccount = emailAccounts.accounts.find((a: any) => a.id === selectedConversation.emailAccountId);
            gmail.downloadAttachment(messageId, attachmentId, filename, mimeType, convAccount?.email);
          }}
          onOpenAttachment={isIonos ? undefined : (messageId: string, attachmentId: string, _filename: string, mimeType: string) => {
            const convAccount = emailAccounts.accounts.find((a: any) => a.id === selectedConversation.emailAccountId);
            gmail.openAttachment(messageId, attachmentId, mimeType, convAccount?.email);
          }}
          onCreateTask={(msg) => setEmailForTask(msg)}
          currentStatus={convStatus.getStatus(selectedConversation.id) as any}
          onSetStatus={(status) => handleSetStatusAndAdvance(status)}
          notes={convStatus.getNotes(selectedConversation.id)}
          onAddNote={(content) => convStatus.addNote(selectedConversation.id, effectiveUserId, currentUserName, content, [])}
          onDeleteNote={(noteId) => convStatus.deleteNote(selectedConversation.id, noteId)}
          currentUserName={currentUserName}
        />
      );
    }

    // WhatsApp oder SMS Nachrichten-Ansicht
    if (selectedConversation.channel === 'whatsapp' || selectedConversation.channel === 'sms') {
      const convId = selectedConversation.originalId;
      const msgConv = convId
        ? messaging.conversations.find(c => c.conversationId === convId)
        : null;

      return (
        <MessagingArea
          conversation={msgConv || null}
          messages={messaging.activeMessages}
          sending={messaging.sending}
          config={messaging.config}
          currentUserId={effectiveUserId}
          onSendMessage={messaging.sendMessage}
          onBack={handleBack}
        />
      );
    }

    // Telefon-Anruf Detail-Ansicht (Mitte)
    if (selectedConversation.channel === 'telefon' && selectedPhoneCall) {
      return (
        <div className="flex-1 flex flex-col h-full">
          <CallDetailPanel
            call={selectedPhoneCall}
            onUpdateNotes={phone.updateCallNotes}
            onRequestCallback={phone.requestCallback}
            onMarkCallbackDone={phone.markCallbackCompleted}
            onClose={handleBack}
          />
        </div>
      );
    }

    return <EmptyCenter activeChannel={activeChannel} />;
  };

  return (
    <div className="flex h-[calc(100vh-56px)] md:h-[calc(100vh-56px)] overflow-hidden bg-white"
      style={{ height: 'calc(100dvh - 56px - env(safe-area-inset-bottom, 0px))' }}>
      {/* Spalte 1: Kanal-Sidebar (schmal, Hover-Expand) */}
      <ChannelSidebar
        activeChannel={activeChannel}
        onSelectChannel={(ch) => { setActiveChannel(ch); setSelectedConversation(null); setMobileShowDetail(false); }}
        channelCounts={channelCounts}
      />

      {/* Mobile Kanal-Tabs */}
      <div className={`lg:hidden flex flex-col h-full flex-1 ${mobileShowDetail ? 'hidden' : ''}`}>
        <MobileChannelTabs
          activeChannel={activeChannel}
          onSelectChannel={(ch) => { setActiveChannel(ch); setSelectedConversation(null); }}
          channelCounts={channelCounts}
        />
        <ConversationList
          conversations={unifiedConversations}
          activeId={selectedConversation?.id || null}
          onSelect={handleSelectConversation}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          searchQuery={searchQuery}
          onSearchQuery={setSearchQuery}
          onNewChat={() => setShowNewChat(true)}
          onNewEmail={handleNewEmail}
          onNewWhatsApp={() => setShowNewWhatsApp(true)}
          onNewSMS={() => setShowNewSMS(true)}
          onNewCall={() => setShowPhoneDialer(true)}
          activeChannel={activeChannel}
          emailFolder={emailFolder}
          onEmailFolder={setEmailFolder}
          onRefreshEmails={handleRefreshEmails}
          emailsLoading={gmail.loadingMessages || gmail.loading}
          loadProgress={gmail.loadProgress}
          emailAccounts={emailAccounts.accounts}
          selectedEmailAccountId={emailAccounts.selectedAccountId}
          onSelectEmailAccount={(id) => emailAccounts.setSelectedAccountId(id)}
          onOpenFilters={() => setShowEmailFilters(true)}
          onOpenSettings={() => setShowEmailSettings(true)}
          onQuickStatus={(convId, status) => { convStatus.setStatus(convId, status, effectiveUserId, currentUserName); toast.success(status === 'erledigt' ? 'Erledigt' : status === 'spaeter' ? 'Auf Sp\u00e4ter verschoben' : 'Zur\u00fcck zu Offen'); }}
          lastSyncTime={gmail.lastSyncTime}
          hasMore={gmail.hasMore}
          onLoadMore={gmail.loadMoreMessages}
          loadingMore={gmail.loadingMessages}
          phoneLoading={phone.loading}
        />
      </div>

      {/* Mobile Detail-Ansicht */}
      {mobileShowDetail && (
        <div className="lg:hidden flex-1 flex flex-col h-full">
          {renderCenter()}
        </div>
      )}

      {/* Desktop: Spalte 2 - Unterhaltungsliste */}
      <div className="hidden lg:flex lg:flex-col h-full">
        <ConversationList
          conversations={unifiedConversations}
          activeId={selectedConversation?.id || null}
          onSelect={handleSelectConversation}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          searchQuery={searchQuery}
          onSearchQuery={setSearchQuery}
          onNewChat={() => setShowNewChat(true)}
          onNewEmail={handleNewEmail}
          onNewWhatsApp={() => setShowNewWhatsApp(true)}
          onNewSMS={() => setShowNewSMS(true)}
          onNewCall={() => setShowPhoneDialer(true)}
          activeChannel={activeChannel}
          emailFolder={emailFolder}
          onEmailFolder={setEmailFolder}
          onRefreshEmails={handleRefreshEmails}
          emailsLoading={gmail.loadingMessages || gmail.loading}
          loadProgress={gmail.loadProgress}
          emailAccounts={emailAccounts.accounts}
          selectedEmailAccountId={emailAccounts.selectedAccountId}
          onSelectEmailAccount={(id) => emailAccounts.setSelectedAccountId(id)}
          onOpenFilters={() => setShowEmailFilters(true)}
          onOpenSettings={() => setShowEmailSettings(true)}
          onQuickStatus={(convId, status) => { convStatus.setStatus(convId, status, effectiveUserId, currentUserName); toast.success(status === 'erledigt' ? 'Erledigt' : status === 'spaeter' ? 'Auf Sp\u00e4ter verschoben' : 'Zur\u00fcck zu Offen'); }}
          lastSyncTime={gmail.lastSyncTime}
          hasMore={gmail.hasMore}
          onLoadMore={gmail.loadMoreMessages}
          loadingMore={gmail.loadingMessages}
          phoneLoading={phone.loading}
        />
      </div>

      {/* Desktop: Spalte 3 - Nachrichtenbereich (Mitte) */}
      <div className="hidden lg:flex flex-1 flex-col h-full min-w-0">
        {renderCenter()}
      </div>

      {/* Desktop: Spalte 4 - Kontaktdetails (rechts) */}
      <ContactPanel
        conversation={selectedConversation}
        currentStatus={selectedConversation ? convStatus.getStatus(selectedConversation.id) as any : 'offen'}
        statusChangedBy={selectedConversation ? convStatus.getStatusEntry(selectedConversation.id)?.updatedByName : undefined}
        statusChangedAt={selectedConversation ? convStatus.getStatusEntry(selectedConversation.id)?.updatedAt : undefined}
        onSetStatus={(status) => selectedConversation && handleSetStatusAndAdvance(status)}
        notes={selectedConversation ? convStatus.getNotes(selectedConversation.id) : []}
        onAddNote={(content, mentions) => selectedConversation && convStatus.addNote(selectedConversation.id, effectiveUserId, currentUserName, content, mentions)}
        onDeleteNote={(noteId) => selectedConversation && convStatus.deleteNote(selectedConversation.id, noteId)}
        assignments={selectedConversation ? convStatus.getAssignments(selectedConversation.id) : []}
        onAssign={(userId) => selectedConversation && convStatus.assignTo(selectedConversation.id, userId, effectiveUserId)}
        onUnassign={(userId) => selectedConversation && convStatus.unassign(selectedConversation.id, userId)}
        teamMembers={contactPanelTeamMembers}
        currentUserId={effectiveUserId}
        docAI={docAI}
        selectedConversationMessages={selectedConversation?.channel === 'email' ? gmail.messages.filter(m => m.threadId === (selectedConversation?.originalId || selectedConversation?.emailThreadId)) : []}
      />

      {/* Dialoge */}
      {showNewChat && (
        <NewChatDialog onClose={() => setShowNewChat(false)} onCreateChat={handleCreateChat} />
      )}
      {showCompose && (
        <ComposeEmailDialog
          onClose={() => { setShowCompose(false); setReplyToEmail(null); setForwardEmail(null); }}
          onSend={handleSendEmail}
          replyTo={replyToEmail}
          forwardMsg={forwardEmail}
          connectedEmail={gmail.connectedEmail}
          emailAccounts={emailAccounts.accounts}
          selectedAccountId={emailAccounts.selectedAccountId}
          onSelectAccount={(id) => emailAccounts.setSelectedAccountId(id)}
        />
      )}
      {/* E-Mail-Filter Dialog */}
      {showEmailFilters && effectiveAccountId && (
        <EmailFilterDialog
          open={showEmailFilters}
          onClose={() => setShowEmailFilters(false)}
          filters={emailFiltersHook.filters}
          accountId={effectiveAccountId}
          onSave={emailFiltersHook.saveFilter}
          onDelete={emailFiltersHook.deleteFilter}
        />
      )}
      {/* E-Mail-Einstellungen Dialog */}
      {showEmailSettings && (
        <EmailSettingsPanel
          open={showEmailSettings}
          onClose={() => setShowEmailSettings(false)}
          accounts={emailAccounts.accounts}
          onUpdateAccount={async (accountId, updates) => {
            const resp = await fetch('/api/email-api', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ action: 'updateAccount', accountId, updates }),
            });
            return resp.json();
          }}
          onTestConnection={async (accountId) => {
            const resp = await fetch('/api/email-api', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ action: 'testConnection', accountId }),
            });
            return resp.json();
          }}
          signatures={emailSignaturesHook.signatures}
          onSaveSignature={emailSignaturesHook.saveSignature}
          onReloadAccounts={emailAccounts.reload}
        />
      )}
      {/* E-Mail zu Aufgabe Dialog */}
      {emailForTask && (
        <CreateTaskFromEmailDialog
          open={!!emailForTask}
          onClose={() => setEmailForTask(null)}
          email={emailForTask}
          currentUserId={effectiveUserId}
          onSuccess={(aufgabeId) => {
            setEmailForTask(null);
            toast.success('Aufgabe erstellt', { description: 'Die Aufgabe wurde aus der E-Mail erstellt.' });
          }}
        />
      )}
      {/* Neue WhatsApp-Nachricht Dialog */}
      {showNewWhatsApp && (
        <NewMessageDialog
          channel="whatsapp"
          onClose={() => setShowNewWhatsApp(false)}
          onSend={messaging.sendMessage}
        />
      )}
      {/* Neue SMS-Nachricht Dialog */}
      {showNewSMS && (
        <NewMessageDialog
          channel="sms"
          onClose={() => setShowNewSMS(false)}
          onSend={messaging.sendMessage}
        />
      )}
      {/* Telefon-Wähler */}
      {showPhoneDialer && (
        <PhoneDialer
          onCall={(number, name) => {
            phone.makeCall(number, name);
            setShowPhoneDialer(false);
          }}
          onClose={() => setShowPhoneDialer(false)}
        />
      )}
      {/* Eingehender Anruf Popup */}
      {phone.incomingCall && (
        <IncomingCallPopup
          call={phone.incomingCall}
          onAnswer={phone.answerCall}
          onReject={phone.rejectCall}
        />
      )}
      {/* Aktiver Anruf Overlay */}
      {phone.activeCall && (
        <ActiveCallView
          call={phone.activeCall}
          onEnd={phone.endCall}
          onMute={phone.toggleMute}
          onHold={phone.toggleHold}
          onRecord={phone.toggleRecording}
          onTransfer={phone.transferCall}
          extensions={phone.extensions}
        />
      )}
    </div>
  );
}

export default function KommunikationPage() {
  return (
    <KommunikationErrorBoundary>
      <KommunikationPageInner />
    </KommunikationErrorBoundary>
  );
}
